/**
 * StartBRRPage Component
 * Configure and start a new Batch Record Review
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  PlayCircle,
  Plus,
  Loader2,
  FileText,
  Upload,
  File,
  Eye,
  Trash2,
  ChevronRight,
  ChevronDown,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Clock,
  X,
} from 'lucide-react';
import { SearchableDropdown, ProductComparisonPanel } from '../components';
import { PdfHighlightModal } from '../components/PdfHighlightModal';
import { useInspectionLotSearch, useInspectionLotInfo, useBatchIdSearch, useBatchSearch, useProductSearch, usePackagedProducts } from '../hooks';
import { useMaterialSearch } from '../hooks/useMaterialSearch';
import { useVendorBatchSearch } from '../hooks/useVendorBatchSearch';
import { BRRApiClient } from '../services/api';
import { SessionAuditClient } from '../services/sessionApi';
import { SessionStatus } from '../services/sessionStatus';
import { BRRLevel, DelayReason, DocumentCounts, EnrichedProduct, PackagedProduct, Priority, BRRReviewSummary, BRRConflictCheckResponse, BRRConflictExactMatch, BRRConflictPartialMatch } from '../services/types';
import { formatTime } from '../utils/formatDate';
import { formatBatchId } from '../utils/formatNumber';
import { FEATURE_FLAGS } from '../config/featureFlags';

// Type Definitions
interface InspectionLot {
  id: string;
  batchId: string;
  status: string;
}

interface Document {
  id: string;
  name: string;
  type?: string;
  size?: string;
  uploadTime?: string;
  uploadedAt?: string;
  blobName?: string;  // Blob storage name for deletion (e.g., user_name/filename.pdf)
  url?: string;       // Blob storage URL
  documentType?: string; // Detected/selected document type id (e.g., 'coc', 'coa', 'combined')
  documentTypes?: string[]; // Sub-types when documentType is 'combined' (e.g., ['coa', 'coc'])
  wasExtracted?: boolean; // True if document was loaded from Neo4j (already extracted)
  needsUpload?: boolean; // True for cross-batch clone placeholders that need actual file upload
  templateSourceBlobName?: string; // Original blob name from template (for reference)
}

export interface StartBRROptions {
  batchId: string;
  lotId: string | null;
  selectedLotIds?: string[];
  productName: string;
  batchType?: 'direct' | 'indirect';
  indirectTransportLotId?: string | null;
  priority?: Priority;
  selectedDocuments?: string[];
  brrLevel?: BRRLevel;
  documentCounts?: DocumentCounts;
  existingDocumentCounts?: DocumentCounts;  // Counts of already-extracted docs for correct indexing
  productUid?: string | null;
  documentTypeMap?: Record<string, string | string[]>;
  destinationCountry?: string;
  delayReason?: string | null;
  delayDetails?: string | null;
  delayActions?: string | null;
  delayCmoName?: string | null;
  expectedReleaseDate?: string | null;
  alreadyExtractedBlobs?: string[];  // Blob names to skip (already extracted in saved draft)
}

interface StartBRRPageProps {
  onStart: (options: StartBRROptions) => void | Promise<void>;
  onCancel: () => void;
  onSessionCreated?: (sessionId: string) => void;  // Callback when session is created
  onBatchIdChange?: (batchId: string) => void;  // Callback when batch ID changes (for persistence across navigation)
  onBatchTypeChange?: (batchType: 'direct' | 'indirect') => void;  // Callback when batch type changes (for sync with Form page)
  onResumeSession?: (sessionId: string) => void;  // Callback to resume an existing session (for conflict resolution)
  apiClient: BRRApiClient;
  sessionClient: SessionAuditClient;
  sessionId: string;
  userEmail: string;
  batchId?: string;  // Batch ID restored from session (for loading saved state on refresh)
  blockNavigation?: (message: string) => void;
  unblockNavigation?: (error?: string | null) => void;
  isNavigationBlocked?: boolean;
  isActive?: boolean;
}

interface RequiredDocument {
  id: string;
  name: string;
  description: string;
  icon: any;
  required: boolean;
}

const PACKAGING_SUBTYPE_IDS = ['packagingLabel', 'packagingFoil', 'packagingLeaflet', 'packagingFoldingBox', 'packagingPrintedPkg', 'packagingPackaging'];

const COMBINED_DOC_TYPE = 'combined';

const StartBRRPageComponent: React.FC<StartBRRPageProps> = ({ onStart, onCancel, onSessionCreated, onBatchIdChange, onBatchTypeChange, onResumeSession, apiClient, sessionClient, sessionId: initialSessionId, userEmail, batchId: initialBatchId, blockNavigation, unblockNavigation, isNavigationBlocked, isActive }) => {
  // Ref to track previous sessionId to detect actual changes (not just re-renders)
  const previousSessionIdRef = useRef<string | null>(null);

  // Refs to prevent useEffects from clearing restored state
  const prevBrrLevelRef = useRef<BRRLevel | null | undefined>(undefined);
  const restoredProductRef = useRef<string | null>(null);
  // Ref to prevent stale lotInfoData from re-populating fields after "New" button clears form
  const justClearedRef = useRef<boolean>(false);
  // Ref to track previous selectedLotId for detecting new lot selection after form clear
  const prevSelectedLotIdRef = useRef<string | null | undefined>(undefined);

  // Feature flag: Controlled via centralized config (src/config/featureFlags.ts)
  const { ENABLE_SAVE_FUNCTIONALITY } = FEATURE_FLAGS;

  // State for save functionality
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string>('');
  const [activeSessionId, setActiveSessionId] = useState<string>(initialSessionId || '');

  // Initial page loading state - true when we have session data to restore
  // and are waiting for both loadSavedState AND lotInfoData to complete
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(
    !!(initialSessionId && initialBatchId)
  );
  const [savedStateLoaded, setSavedStateLoaded] = useState<boolean>(false);

  // State for batch selection
  const [batchNumber, setBatchNumber] = useState<string>(initialBatchId || '');
  const [batchSearchText, setBatchSearchText] = useState<string>('');
  const [inspectionLots, setInspectionLots] = useState<InspectionLot[]>([]);
  const [inspectionSearchText, setInspectionSearchText] = useState<string>('');
  const [selectedLotId, setSelectedLotId] = useState<string | null>(null);
  const [product, setProduct] = useState<string>('');
  const [productSearchText, setProductSearchText] = useState<string>('');
  const [productCandidates, setProductCandidates] = useState<EnrichedProduct[]>([]);
  const [selectedProductDetail, setSelectedProductDetail] = useState<EnrichedProduct | null>(null);
  const [selectedProductUid, setSelectedProductUid] = useState<string | null>(null);
  const [showPreviousMatches, setShowPreviousMatches] = useState<boolean>(false);
  const [productTypeFilter, setProductTypeFilter] = useState<'all' | 'packaged' | 'pharmaceutical'>('all');
  const [destinationCountry, setDestinationCountry] = useState<string>('');
  const [countrySearchText, setCountrySearchText] = useState<string>('');
  const [materialDescription, setMaterialDescription] = useState<string>('');
  const [priority, setPriority] = useState<Priority>('medium');
  const [delayReason, setDelayReason] = useState<DelayReason | ''>('');
  const [delayDetails, setDelayDetails] = useState('');
  const [delayActions, setDelayActions] = useState('');
  const [delayCmoName, setDelayCmoName] = useState('');
  const [expectedReleaseDate, setExpectedReleaseDate] = useState<string>('');
  const [brrLevel, setBrrLevel] = useState<BRRLevel | null>(null);

  // State for packaged medicinal product (Level 2 only)
  const [selectedPMP, setSelectedPMP] = useState<PackagedProduct | null>(null);
  const [pmpSearchText, setPmpSearchText] = useState('');
  const [pendingPmpId, setPendingPmpId] = useState<string | null>(null);

  // State for batch ID search method
  const [searchMethod, setSearchMethod] = useState<'inspection-lot' | 'batch-id' | 'material-id' | 'vendor-batch-id'>('inspection-lot');
  const [selectedLotIds, setSelectedLotIds] = useState<string[]>([]);
  const [batchIdSearchText, setBatchIdSearchText] = useState<string>('');
  const [batchIdConfirmed, setBatchIdConfirmed] = useState(false);
  const [fieldSearchText, setFieldSearchText] = useState<string>('');
  const [materialSearchText, setMaterialSearchText] = useState<string>('');
  const [selectedMaterialId, setSelectedMaterialId] = useState<string | null>(null);
  const [materialId, setMaterialId] = useState<string | null>(null);
  const [selectedVendorBatchId, setSelectedVendorBatchId] = useState<string | null>(null);
  const [vendorBatchId, setVendorBatchId] = useState<string | null>(null);

  // State for batch type and indirect transport
  const [batchType, setBatchType] = useState<'direct' | 'indirect'>('direct');
  const [indirectTransportLotId, setIndirectTransportLotId] = useState<string | null>(null);
  const [indirectLotSearchText, setIndirectLotSearchText] = useState<string>('');

  // State for document upload
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<Document[]>([]);
  const [previewBlobName, setPreviewBlobName] = useState<string | null>(null);
  // Track counts of documents already extracted (loaded from Neo4j).
  // Used for existing_document_counts when resuming a draft - only counts
  // saved docs, not newly uploaded ones that haven't been extracted yet.
  const [savedDocumentCounts, setSavedDocumentCounts] = useState<DocumentCounts>({});
  const [confirmationChecked, setConfirmationChecked] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [isClearing, setIsClearing] = useState<boolean>(false);
  const [uploadInfo, setUploadInfo] = useState<string>('');
  const [isNewPressed, setIsNewPressed] = useState<boolean>(false);

  // State for clone from existing BRR
  const [existingReviews, setExistingReviews] = useState<BRRReviewSummary[]>([]);
  const [existingReviewsLoading, setExistingReviewsLoading] = useState(false);
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [selectedCloneSource, setSelectedCloneSource] = useState<BRRReviewSummary | null>(null);
  const [isCloning, setIsCloning] = useState(false);
  const [cloneError, setCloneError] = useState('');
  const [clonedFromSessionId, setClonedFromSessionId] = useState<string | null>(null);
  const [reloadTrigger, setReloadTrigger] = useState(0);

  // State for conflict checking (batch + lot uniqueness)
  const [conflictCheck, setConflictCheck] = useState<BRRConflictCheckResponse | null>(null);
  const [isCheckingConflict, setIsCheckingConflict] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [isDeletingConflict, setIsDeletingConflict] = useState(false);
  const [conflictDeleteError, setConflictDeleteError] = useState<string | null>(null);
  const [isResumingSession, setIsResumingSession] = useState(false);

  // Document type selection derived from per-upload type assignments
  const selectedDocumentIds = useMemo(() => {
    const types: string[] = [];
    uploadedDocuments.forEach(d => {
      if (d.documentType === COMBINED_DOC_TYPE) {
        types.push(...(d.documentTypes || []));
      } else if (d.documentType) {
        types.push(d.documentType);
      }
    });
    return [...new Set(types)];
  }, [uploadedDocuments]);

  // Multi-instance document types that can have multiple documents per batch
  const MULTI_INSTANCE_DOC_TYPES = ['bulkCoa', 'bulkCoc', 'delivery', 'bom', 'deviation', 'transport', 'other'];

  // Map frontend document type IDs to backend DocumentType enum values.
  // Only types with a valid backend enum value are included — serialization
  // and aggregation are excluded (no backend enum entry) and fall back to
  // AI classification.
  const FRONTEND_TO_BACKEND_DOC_TYPE: Record<string, string> = {
    coc: 'COC',
    coa: 'COA',
    bulkCoc: 'BULK_COC',
    bulkCoa: 'BULK_COA',
    delivery: 'DELIVERY_NOTE',
    bom: 'BILL_OF_MATERIALS',
    transport: 'TRANSPORT_REPORT',
    packagingLabel: 'PACKAGING_MATERIALS__LABEL',
    packagingFoil: 'PACKAGING_MATERIALS__FOIL',
    packagingLeaflet: 'PACKAGING_MATERIALS__LEAFLET',
    packagingFoldingBox: 'PACKAGING_MATERIALS__FOLDING_BOX',
    packagingPrintedPkg: 'PACKAGING_MATERIALS__PRINTED_PACKAGING',
    packagingPackaging: 'PACKAGING_MATERIALS__PACKAGING',
    deviation: 'DEVIATION_REPORT',
    other: 'OTHER',
  };

  // Compute counts for all multi-instance document types
  const documentCounts = useMemo(() => {
    const counts: DocumentCounts = {};
    MULTI_INSTANCE_DOC_TYPES.forEach(docType => {
      const count = uploadedDocuments.filter(d => {
        if (d.documentType === COMBINED_DOC_TYPE) return (d.documentTypes || []).includes(docType);
        return d.documentType === docType;
      }).length;
      if (count > 0) {
        counts[docType] = count;
      }
    });
    return counts;
  }, [uploadedDocuments]);

  // Single-document types that can only be assigned once per batch
  const SINGLE_DOCUMENT_TYPES = ['coa', 'coc', ...PACKAGING_SUBTYPE_IDS];

  // Compute which single-document types are assigned and check for duplicates
  const { assignedSingleTypes, duplicateWarnings } = useMemo(() => {
    const typeCounts: Record<string, number> = {};
    uploadedDocuments.forEach(doc => {
      const effectiveTypes = doc.documentType === COMBINED_DOC_TYPE
        ? (doc.documentTypes || [])
        : (doc.documentType ? [doc.documentType] : []);
      effectiveTypes.forEach(dt => {
        if (SINGLE_DOCUMENT_TYPES.includes(dt)) {
          typeCounts[dt] = (typeCounts[dt] || 0) + 1;
        }
      });
    });

    const assigned = new Set(Object.keys(typeCounts));
    const warnings: string[] = [];

    if (typeCounts['coa'] > 1) {
      warnings.push('Multiple documents flagged as Certificate of Analysis (CoA). Only one CoA is allowed per batch.');
    }
    if (typeCounts['coc'] > 1) {
      warnings.push('Multiple documents flagged as Certificate of Conformance (CoC). Only one CoC is allowed per batch.');
    }
    for (const subId of PACKAGING_SUBTYPE_IDS) {
      if (typeCounts[subId] > 1) {
        const doc = requiredDocuments.find(d => d.id === subId);
        warnings.push(`Multiple documents flagged as ${doc?.name || subId}. Only one is allowed per batch.`);
      }
    }

    return { assignedSingleTypes: assigned, duplicateWarnings: warnings };
  }, [uploadedDocuments]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync batchNumber changes to parent (BRRApp) for persistence across navigation
  useEffect(() => {
    if (batchNumber && onBatchIdChange) {
      onBatchIdChange(batchNumber);
    }
  }, [batchNumber, onBatchIdChange]);

  // Use the API search hook for inspection lots
  const {
    results: inspectionLotResults,
    loading: inspectionLotsLoading,
    error: inspectionLotsError
  } = useInspectionLotSearch({
    apiClient,
    query: inspectionSearchText,
    debounceMs: 500,
    minQueryLength: 3,
  });

  // Use the API info hook for selected inspection lot
  const {
    data: lotInfoData,
    loading: lotInfoLoading,
    error: lotInfoError
  } = useInspectionLotInfo({
    apiClient,
    inspectionLotId: selectedLotId,
    brrLevel
  });

  // Use the product search hook for manual product lookup
  const {
    results: productSearchResults,
    loading: productSearchLoading,
    hasMore: productSearchHasMore,
    loadMore: productSearchLoadMore,
    loadingMore: productSearchLoadingMore,
  } = useProductSearch({
    apiClient,
    query: productSearchText,
    debounceMs: 400,
    minQueryLength: 2,
    limit: 20
  });

  // Use the batch ID search hook (gets inspection lots for a given batch ID)
  const {
    results: batchIdResults,
    loading: batchIdLoading,
    error: batchIdError
  } = useBatchIdSearch({
    apiClient,
    batchId: batchIdSearchText,
    debounceMs: 500,
    minQueryLength: 3,
  });

  // Use the batch search hook (autocomplete for batch IDs by prefix)
  const {
    results: batchSearchResults,
    loading: batchSearchLoading,
    error: batchSearchError
  } = useBatchSearch({
    apiClient,
    query: searchMethod === 'batch-id' ? batchIdSearchText : '',
    debounceMs: 400,
    minQueryLength: 3,
  });

  // Use vendor batch search hook for vendor-batch-id mode (returns vendorBatchId + batchId)
  const {
    results: vendorBatchResults,
    loading: vendorBatchLoading,
    error: vendorBatchError,
  } = useVendorBatchSearch({
    apiClient,
    query: searchMethod === 'vendor-batch-id' ? fieldSearchText : '',
    debounceMs: 400,
    minQueryLength: 3,
  });

  // Material search hook for material-id mode (step 1: find materials)
  const {
    results: materialResults,
    loading: materialLoading,
    error: materialError,
  } = useMaterialSearch({
    apiClient,
    query: searchMethod === 'material-id' ? materialSearchText : '',
    debounceMs: 400,
    minQueryLength: 3,
  });

  // For material-id mode step 2: fetch batches for the selected material
  const {
    results: materialBatchResults,
    loading: materialBatchLoading,
  } = useBatchSearch({
    apiClient,
    query: selectedMaterialId || '',
    field: 'material_id',
    debounceMs: 100,
    minQueryLength: 3,
  });

  // For vendor-batch-id mode step 2: fetch batches for the selected vendor batch ID
  const {
    results: vendorBatchBatchResults,
    loading: vendorBatchBatchLoading,
  } = useBatchSearch({
    apiClient,
    query: selectedVendorBatchId || '',
    field: 'vendor_batch_id',
    debounceMs: 100,
    minQueryLength: 3,
  });

  // Fetch packaged medicinal products for Level 2 pharmaceutical products
  const {
    data: packagedProducts,
    loading: pmpLoading,
    error: pmpError
  } = usePackagedProducts({
    apiClient,
    pharmaceuticalProductName: product || null,
    enabled: brrLevel === 'level2' && lotInfoData?.productType === 'pharmaceutical' && !!product
  });

  // Use the API search hook for indirect transport inspection lot
  const {
    results: indirectLotResults,
    loading: indirectLotLoading,
    error: indirectLotError
  } = useInspectionLotSearch({
    apiClient,
    query: indirectLotSearchText,
    debounceMs: 500,
    minQueryLength: 3,
  });

  // Computed value: whether any lots are selected (works for both search methods)
  const hasSelectedLots = searchMethod === 'inspection-lot'
    ? selectedLotId !== null
    : selectedLotIds.length > 0;

  // Computed value: current lots for conflict checking
  const currentLots = useMemo(() => {
    if (searchMethod === 'inspection-lot' && selectedLotId) {
      return [selectedLotId];
    } else if (searchMethod !== 'inspection-lot' && selectedLotIds.length > 0) {
      return selectedLotIds;
    }
    return [];
  }, [searchMethod, selectedLotId, selectedLotIds]);

  // Check for conflicts when batch + lots change
  useEffect(() => {
    const checkConflict = async () => {
      // Skip conflict check if user is resuming an existing session
      if (isResumingSession) {
        return;
      }

      // Clear previous conflict state
      setConflictCheck(null);

      // Need both batch number and lots to check
      if (!batchNumber || currentLots.length === 0) {
        return;
      }

      setIsCheckingConflict(true);
      try {
        const result = await apiClient.checkConflict(
          batchNumber,
          currentLots,
          activeSessionId // Exclude own session from check
        );
        setConflictCheck(result);

        // Auto-show modal if exact match found
        if (result.exactMatch) {
          setShowConflictModal(true);
        }
      } catch (error) {
        console.error('[StartBRRPage] Conflict check failed:', error);
        // Don't block user if conflict check fails - just log it
      } finally {
        setIsCheckingConflict(false);
      }
    };

    checkConflict();
  }, [batchNumber, currentLots, activeSessionId, apiClient, isResumingSession]);

  // Computed value: whether there's a blocking conflict (exact match)
  const hasBlockingConflict = conflictCheck?.exactMatch !== null && conflictCheck?.exactMatch !== undefined;

  // Auto-populate fields when lot info data arrives
  useEffect(() => {
    // For non-inspection-lot modes: set selectedLotId to first selected lot
    if (searchMethod !== 'inspection-lot' && selectedLotIds.length > 0) {
      const firstLotId = selectedLotIds[0];
      if (firstLotId !== selectedLotId) {
        setSelectedLotId(firstLotId);
      }
      // Don't return here - let auto-population happen below
    }

    // Auto-populate from lotInfoData (works for both search methods)
    if (!lotInfoData) {
      // Update tracking ref even when no data
      prevSelectedLotIdRef.current = selectedLotId;
      return;
    }

    // Clear justClearedRef when user selects a NEW lot after clearing
    // (i.e., selectedLotId changed from null to a non-null value)
    if (justClearedRef.current) {
      const wasNull = prevSelectedLotIdRef.current === null;
      const isNowNonNull = selectedLotId !== null;
      if (wasNull && isNowNonNull) {
        // User actively selected a new lot after the form was cleared
        justClearedRef.current = false;
      } else {
        // Still in the clearing cascade or stale data - skip population
        prevSelectedLotIdRef.current = selectedLotId;
        return;
      }
    }
    prevSelectedLotIdRef.current = selectedLotId;

    // Batch ID - only auto-populate in inspection-lot mode
    // In batch-id mode, preserve the user's search term (set by sync effect below)
    if (lotInfoData.batchId && searchMethod === 'inspection-lot') {
      setBatchNumber(lotInfoData.batchId);
      setBatchSearchText(lotInfoData.batchId);
    }

    // Country
    if (lotInfoData.country) {
      setDestinationCountry(lotInfoData.country);
      setCountrySearchText(lotInfoData.country);
    } else {
      setDestinationCountry('');
      setCountrySearchText('');
    }

    // Material Description
    if (lotInfoData.materialDescription) {
      setMaterialDescription(lotInfoData.materialDescription);
    } else {
      setMaterialDescription('');
    }

    // Material ID and Vendor Batch ID (for session details / dashboard search)
    if (lotInfoData.materialId) {
      setMaterialId(lotInfoData.materialId);
    } else {
      setMaterialId(null);
    }
    if (lotInfoData.vendorBatchId) {
      setVendorBatchId(lotInfoData.vendorBatchId);
    } else {
      setVendorBatchId(null);
    }

    // Product based on match type — always populate candidates so dropdown can show them
    if (lotInfoData.products.length > 0) {
      setProductCandidates(lotInfoData.products);
    } else {
      setProductCandidates([]);
    }

    // If we just restored a product from saved state, don't overwrite the name/searchText
    // but still populate selectedProductDetail so the comparison panel renders
    if (restoredProductRef.current) {
      const restoredName = restoredProductRef.current;
      restoredProductRef.current = null;
      const match = lotInfoData.products.find(p => p.name === restoredName);
      if (match) {
        setSelectedProductDetail(match);
        setSelectedProductUid(match.uid);
      }
      return;
    }

    if ((lotInfoData.matchType === 'exact' || lotInfoData.matchType === 'confirmed') && lotInfoData.products.length >= 1) {
      // Auto-select the primary match (confirmed/exact product)
      const primary = lotInfoData.products.find(p => p.matchSource === lotInfoData.matchType) || lotInfoData.products[0];
      setProduct(primary.name);
      setProductSearchText(primary.name);
      setSelectedProductDetail(primary);
      setSelectedProductUid(primary.uid);
    } else if (lotInfoData.matchType === 'candidates' && lotInfoData.products.length > 0) {
      setProduct('');
      setProductSearchText('');
      setSelectedProductDetail(null);
      setSelectedProductUid(null);
    } else {
      setProduct('');
      setProductSearchText('');
      setSelectedProductDetail(null);
      setSelectedProductUid(null);
    }
  }, [searchMethod, selectedLotIds, lotInfoData, selectedLotId]);

  // Clear product and PMP state when brrLevel changes (prevents stale data from different type)
  // Skip clearing on initial mount or when restoring saved state (prevRef is undefined or null)
  useEffect(() => {
    if (prevBrrLevelRef.current !== undefined && prevBrrLevelRef.current !== null && prevBrrLevelRef.current !== brrLevel) {
      // User changed brrLevel - clear product
      setProduct('');
      setProductSearchText('');
      setProductCandidates([]);
      setSelectedPMP(null);
      setPmpSearchText('');
    }
    prevBrrLevelRef.current = brrLevel;
  }, [brrLevel]);

  // Clear PMP when the selected product changes
  useEffect(() => {
    setSelectedPMP(null);
    setPmpSearchText('');
  }, [product]);

  // Auto-populate destination country when a PMP is selected (if it has market data)
  useEffect(() => {
    if (!selectedPMP) return;
    if (selectedPMP.markets.length) {
      const m = selectedPMP.markets[0];
      const display = `${m.countryName} (${m.countryCode})`;
      setDestinationCountry(display);
      setCountrySearchText(display);
    } else {
      setDestinationCountry('');
      setCountrySearchText('');
    }
  }, [selectedPMP]);

  // Restore PMP selection after hook data loads (for save/load)
  useEffect(() => {
    if (pendingPmpId && packagedProducts && packagedProducts.length > 0) {
      const match = packagedProducts.find(p => p.id === pendingPmpId);
      if (match) {
        setSelectedPMP(match);
        setPmpSearchText(match.name);
      }
      setPendingPmpId(null);
    }
  }, [packagedProducts, pendingPmpId]);

  // Sync batchNumber with batchIdSearchText in batch-id search mode.
  // Triggers on search text change (not only when results arrive) so that
  // features like clone-from-template can query by batch even when no
  // inspection lots exist in the graph yet.
  useEffect(() => {
    // Skip sync if form was just cleared (prevents stale data re-populating after "New" click)
    if (justClearedRef.current) return;

    if (searchMethod === 'batch-id' && batchIdSearchText && batchIdSearchText.length >= 4) {
      if (batchNumber !== batchIdSearchText) {
        setBatchNumber(batchIdSearchText);
        setBatchSearchText(batchIdSearchText);
      }
    }
  }, [searchMethod, batchIdSearchText, batchNumber]);

  // Transform API results to match the InspectionLot interface
  const inspectionLotOptions = inspectionLotResults.map(result => ({
    id: result.lotId,
    batchId: '',
    status: ''
  }));

  // Product dropdown shows search results only — previous matches shown via toggle button
  // Add a "Load More" sentinel string when more results are available
  const LOAD_MORE_SENTINEL = '__LOAD_MORE__';
  const filteredSearchResults = productTypeFilter === 'all'
    ? productSearchResults
    : productSearchResults.filter(p =>
        productTypeFilter === 'pharmaceutical'
          ? p.productType === 'Pharmaceutical Product'
          : p.productType === 'Packaged Medicinal Product'
      );
  const productOptions: (EnrichedProduct | string)[] = [
    ...filteredSearchResults,
    ...(productSearchHasMore ? [LOAD_MORE_SENTINEL] : []),
  ];

  // Show a "no results for filter" hint when filtering produces empty results
  const filterEmptyButMoreAvailable = productTypeFilter !== 'all' && filteredSearchResults.length === 0 && productSearchResults.length > 0;

  const countryOptions = [
    'Germany (DE)',
    'France (FR)',
    'United Kingdom (UK)',
    'Switzerland (CH)',
    'Italy (IT)',
    'Spain (ES)'
  ];
  const pmpWithoutMarket = selectedPMP && selectedPMP.markets.length === 0;
  const availableCountries = selectedLotId && !pmpWithoutMarket
    ? []
    : countryOptions;

  // Sync props to state when navigating back to this page with existing session data
  // useState only uses initial value on first mount, so we need this effect to update
  // state when props change (e.g., navigating back from Document Validation)
  useEffect(() => {
    // Only sync if we have valid session data from props
    if (initialSessionId && initialBatchId) {
      // Check if we need to update state (props differ from current state)
      const needsSync = activeSessionId !== initialSessionId || batchNumber !== initialBatchId;

      if (needsSync) {
        // Update state to match props
        setActiveSessionId(initialSessionId);
        setBatchNumber(initialBatchId);

        // Reset loading states to trigger a fresh load
        setSavedStateLoaded(false);
        setIsInitialLoading(true);
      }
    }
  }, [initialSessionId, initialBatchId]); // Only depend on props, not state

  // Create session on mount if one doesn't exist
  useEffect(() => {
    const initializeSession = async () => {
      if (!activeSessionId) {
        try {
          const tempBatchId = batchNumber || 'TEMP-' + Date.now();
          const session = await sessionClient.createSession({
            batchId: tempBatchId,
            currentPage: 'start-brr',
          });
          setActiveSessionId(session.id);

          // Notify parent component (BRRApp) about the new session
          if (onSessionCreated) {
            onSessionCreated(session.id);
          }
        } catch (error) {
          console.error('Failed to create session via the session client:', error);
          // Fallback to local session on session client failure
          const fallbackSessionId = `fallback-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          setActiveSessionId(fallbackSessionId);
          if (onSessionCreated) {
            onSessionCreated(fallbackSessionId);
          }
        }
      }
    };
    void initializeSession();
  }, []); // Run once on mount

  // Load saved state on mount
  useEffect(() => {
    let isMounted = true; // Track if component is still mounted

    const loadSavedState = async () => {
      if (!batchNumber || !activeSessionId) {
        setSavedStateLoaded(true); // No saved state to load
        return;
      }

      // Mark that we're loading saved state (used for initial loading indicator)
      setSavedStateLoaded(false);

      // CRITICAL FIX: Clear all form state when sessionId CHANGES (not on initial mount)
      // to prevent stale data from previous session persisting in memory
      const sessionHasChanged = previousSessionIdRef.current && previousSessionIdRef.current !== activeSessionId;

      if (sessionHasChanged && isMounted) {
        setSearchMethod('inspection-lot');
        setSelectedLotId(null);
        setFieldSearchText('');
        setMaterialSearchText('');
        setSelectedMaterialId(null);
        setSelectedVendorBatchId(null);
        setMaterialId(null);
        setVendorBatchId(null);
        setProduct('');
        setDestinationCountry('');
        setMaterialDescription('');
        setIndirectTransportLotId(null);
        setUploadedDocuments([]);
        setSavedDocumentCounts({});
        setConfirmationChecked(false);
        setBatchType('direct');
        setPriority('medium');
        setDelayReason('');
        setDelayDetails('');
        setDelayActions('');
        setExpectedReleaseDate('');
        setLastSaved(null);
        setSelectedPMP(null);
        setPmpSearchText('');
        // Reset initial loading state for new session
        setIsInitialLoading(true);
      }

      // Update the ref to current sessionId for next comparison
      previousSessionIdRef.current = activeSessionId;

      try {
        const savedReview = await apiClient.getSavedReview(batchNumber, activeSessionId);

        // Only update state if component is still mounted
        if (!isMounted) return;

        if (savedReview && savedReview.startPage) {
          // Populate form fields from saved start_page section
          const startPage = savedReview.startPage;

          if (startPage.searchMethod) setSearchMethod(startPage.searchMethod);
          if (startPage.inspectionLotId) {
            setSelectedLotId(startPage.inspectionLotId);
            setInspectionSearchText(startPage.inspectionLotId); // Also set search text for UI display
          }
          if (startPage.selectedLotIds) setSelectedLotIds(startPage.selectedLotIds);
          if (startPage.batchIdSearchText) {
            setBatchIdSearchText(startPage.batchIdSearchText);
            setBatchIdConfirmed(true);
            // Also set batchNumber immediately in batch-id mode to ensure save key is correct
            if (startPage.searchMethod === 'batch-id') {
              setBatchNumber(startPage.batchIdSearchText);
              setBatchSearchText(startPage.batchIdSearchText);
            }
          }
          if (startPage.productName) {
            restoredProductRef.current = startPage.productName; // Mark as restored to prevent auto-populate overwrite
            setProduct(startPage.productName);
            setProductSearchText(startPage.productName); // Also set search text for UI display
          }
          if (startPage.productUid !== undefined) setSelectedProductUid(startPage.productUid ?? null);
          if (startPage.destinationCountry) {
            setDestinationCountry(startPage.destinationCountry);
            setCountrySearchText(startPage.destinationCountry); // Also set search text for UI display
          }
          if (startPage.materialDescription) setMaterialDescription(startPage.materialDescription);
          if (startPage.fieldSearchText) setFieldSearchText(startPage.fieldSearchText);
          if (startPage.materialSearchText) setMaterialSearchText(startPage.materialSearchText);
          if (startPage.selectedMaterialId) setSelectedMaterialId(startPage.selectedMaterialId);
          if (startPage.selectedVendorBatchId) setSelectedVendorBatchId(startPage.selectedVendorBatchId);
          if (startPage.materialId) setMaterialId(startPage.materialId);
          if (startPage.vendorBatchId) setVendorBatchId(startPage.vendorBatchId);
          if (startPage.indirectTransportLotId) {
            setIndirectTransportLotId(startPage.indirectTransportLotId);
            setIndirectLotSearchText(startPage.indirectTransportLotId); // Also set search text for UI display
          }
          if (startPage.uploadedDocuments) {
            // Mark all loaded documents as already extracted (they came from Neo4j)
            const docsWithExtractionStatus = startPage.uploadedDocuments.map(d => ({
              ...d,
              wasExtracted: true
            }));
            setUploadedDocuments(docsWithExtractionStatus);
            // Calculate and store counts of already-extracted documents.
            // This is used for existing_document_counts when adding new docs to a draft.
            const savedCounts: DocumentCounts = {};
            const ALL_DOC_TYPES = ['coa', 'coc', 'packaging', 'bulkCoa', 'bulkCoc', 'delivery', 'bom', 'deviation', 'transport', 'other'];
            ALL_DOC_TYPES.forEach(docType => {
              const count = startPage.uploadedDocuments!.filter(d => {
                if (d.documentType === COMBINED_DOC_TYPE) return (d.documentTypes || []).includes(docType);
                return d.documentType === docType;
              }).length;
              if (count > 0) {
                savedCounts[docType] = count;
              }
            });
            setSavedDocumentCounts(savedCounts);
          }
          if (startPage.confirmationChecked !== undefined) setConfirmationChecked(startPage.confirmationChecked);
          if (startPage.delayReason) setDelayReason(startPage.delayReason);
          if (startPage.delayDetails) setDelayDetails(startPage.delayDetails);
          if (startPage.delayActions) setDelayActions(startPage.delayActions);
          if (startPage.delayCmoName) setDelayCmoName(startPage.delayCmoName);
          if (startPage.expectedReleaseDate) setExpectedReleaseDate(startPage.expectedReleaseDate);
          if (startPage.brrLevel) setBrrLevel(startPage.brrLevel);
          if (startPage.packagedMedicinalProduct) {
            setPendingPmpId(startPage.packagedMedicinalProduct.id);
            setPmpSearchText(startPage.packagedMedicinalProduct.name);
          }

          // Load top-level metadata
          if (savedReview.batchId) setBatchNumber(String(savedReview.batchId));
          if (savedReview.batchType) {
            setBatchType(savedReview.batchType);
            onBatchTypeChange?.(savedReview.batchType);
          }
          if (savedReview.priority) setPriority((savedReview.priority as string) === 'normal' ? 'medium' : savedReview.priority);

          // Set lastSaved timestamp
          if (savedReview.savedAt) {
            setLastSaved(new Date(savedReview.savedAt));
          }

        }

        // Clone audit trail (top-level field, not nested in startPage)
        if (savedReview && savedReview.clonedFromSessionId) {
          setClonedFromSessionId(savedReview.clonedFromSessionId);
        }

        // Mark saved state as loaded (initial loading overlay will continue if lotInfoLoading)
        setSavedStateLoaded(true);
      } catch (error) {
        // If load fails, just log it - don't block the user
        console.warn('Failed to load saved state:', error);
        setSavedStateLoaded(true);
      }
    };

    void loadSavedState();

    // Cleanup: mark component as unmounted
    return () => {
      isMounted = false;
    };
  }, [activeSessionId, batchNumber, reloadTrigger]); // Run when session or batch changes, or after clone

  // Manage initial loading overlay - show "Loading Start BRR Page..." until all initial data is ready
  // This covers both loadSavedState AND the subsequent lotInfoData fetch
  useEffect(() => {
    // If we don't have session data, no initial loading needed
    if (!initialSessionId || !initialBatchId) {
      setIsInitialLoading(false);
      return;
    }

    // While initial loading is active, show the overlay
    if (isInitialLoading) {
      blockNavigation?.('Loading Start BRR Page...');
    }

    // Check if initial loading is complete:
    // 1. Saved state must be loaded
    // 2. If there's a selected lot, lot info must be loaded (not loading)
    const lotInfoReady = !selectedLotId || !lotInfoLoading;
    const initialLoadComplete = savedStateLoaded && lotInfoReady;

    if (initialLoadComplete && isInitialLoading) {
      setIsInitialLoading(false);
      unblockNavigation?.();
    }
  }, [isInitialLoading, savedStateLoaded, lotInfoLoading, selectedLotId, initialSessionId, initialBatchId, blockNavigation, unblockNavigation]);

  // Fetch BRR templates for this material (for clone-from-template feature)
  // Uses materialId to find previous batches of the same product as templates
  useEffect(() => {
    if (!materialId) {
      setExistingReviews([]);
      return;
    }
    const fetchExisting = async () => {
      setExistingReviewsLoading(true);
      try {
        const result = await apiClient.getReviewsByMaterial(materialId, 10);
        const otherReviews = result.reviews.filter(r => r.sessionId !== activeSessionId);
        setExistingReviews(otherReviews);
      } catch (error) {
        console.warn('Failed to fetch BRR templates:', error);
        setExistingReviews([]);
      } finally {
        setExistingReviewsLoading(false);
      }
    };
    void fetchExisting();
  }, [materialId, activeSessionId, apiClient]);

  // Block navigation only during initial search (before any results exist)
  // Skip if initial page loading is in progress (that takes priority)
  useEffect(() => {
    // Don't interfere with initial page loading
    if (isInitialLoading) return;

    const isInitialInspectionLotSearch = inspectionLotsLoading && inspectionLotResults.length === 0;
    const isInitialBatchSearch = batchSearchLoading && batchSearchResults.length === 0;
    const isInitialMaterialSearch = materialLoading && materialResults.length === 0;
    const isInitialMaterialBatchSearch = materialBatchLoading && materialBatchResults.length === 0;
    const isInitialVendorBatchSearch = vendorBatchLoading && vendorBatchResults.length === 0;
    const isInitialVendorBatchBatchSearch = vendorBatchBatchLoading && vendorBatchBatchResults.length === 0;
    const isInitialLotSearch = batchIdLoading && batchIdResults.length === 0;

    const shouldBlock = isInitialInspectionLotSearch || isInitialBatchSearch
      || isInitialMaterialSearch || isInitialMaterialBatchSearch
      || isInitialVendorBatchSearch || isInitialVendorBatchBatchSearch || isInitialLotSearch;

    if (shouldBlock && blockNavigation) {
      blockNavigation('Querying...');
    } else if (!shouldBlock && unblockNavigation) {
      unblockNavigation();
    }
  }, [isInitialLoading, inspectionLotsLoading, inspectionLotResults, batchSearchLoading, batchSearchResults, materialLoading, materialResults, materialBatchLoading, materialBatchResults, vendorBatchLoading, vendorBatchResults, vendorBatchBatchLoading, vendorBatchBatchResults, batchIdLoading, batchIdResults, blockNavigation, unblockNavigation]);

  // Handle cloning an existing BRR as a template for the current session
  const handleClone = useCallback(async () => {
    if (!selectedCloneSource) return;
    // Resolve session ID: use existing, or generate a unique one for localhost/test
    const sessionId = activeSessionId || `clone-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    // Determine if this is a cross-batch clone (template from different batch)
    const isCrossBatch = batchNumber && batchNumber !== selectedCloneSource.batchId;

    setIsCloning(true);
    setCloneError('');
    try {
      const result = await apiClient.cloneBRR({
        sourceBatchId: selectedCloneSource.batchId,
        sourceSessionId: selectedCloneSource.sessionId,
        newSessionId: sessionId,
        // For cross-batch clones, pass the target batch info
        targetBatchId: isCrossBatch ? batchNumber : undefined,
        targetSelectedLotIds: isCrossBatch ? selectedLotIds : undefined,
      });
      // After clone, always switch to the new cloned session
      setActiveSessionId(sessionId);
      // Sync session ID and batch ID to parent so other pages can load the cloned data
      if (onSessionCreated) onSessionCreated(sessionId);
      // Use the target batch (current batch) not the source batch
      if (onBatchIdChange) {
        const targetBatch = isCrossBatch ? batchNumber : selectedCloneSource.batchId;
        if (targetBatch) {
          onBatchIdChange(targetBatch);
        }
      }
      setShowCloneDialog(false);
      setSelectedCloneSource(null);
      setReloadTrigger(prev => prev + 1);
    } catch (error) {
      setCloneError(error instanceof Error ? error.message : 'Failed to clone BRR');
    } finally {
      setIsCloning(false);
    }
  }, [selectedCloneSource, activeSessionId, userEmail, apiClient, onSessionCreated, onBatchIdChange, batchNumber, selectedLotIds]);

  // Required documents configuration
  const requiredDocuments: RequiredDocument[] = [
    { id: 'coc', name: 'Certificate of Conformance (CoC)', description: 'GMP compliance confirmation from CMO', icon: FileText, required: true },
    { id: 'coa', name: 'Certificate of Analysis (CoA)', description: 'Analytical test results from CMO', icon: FileText, required: true },
    { id: 'delivery', name: 'Delivery Note, Packing List, Invoice', description: 'Shipping and delivery documentation', icon: FileText, required: true },
    { id: 'bom', name: 'Bill of Materials', description: 'Bill of materials and packaging evidence', icon: FileText, required: true },
    { id: 'transport', name: 'Transport Report', description: 'Temperature and transport condition monitoring', icon: FileText, required: false },
    { id: 'packagingLabel', name: 'Packaging Material Scan (Label)', description: 'Artwork scan for label materials', icon: FileText, required: false },
    { id: 'packagingFoil', name: 'Packaging Material Scan (Foil)', description: 'Artwork scan for foil materials', icon: FileText, required: false },
    { id: 'packagingLeaflet', name: 'Packaging Material Scan (Leaflet)', description: 'Artwork scan for leaflet materials', icon: FileText, required: false },
    { id: 'packagingFoldingBox', name: 'Packaging Material Scan (Folding Box)', description: 'Artwork scan for folding box materials', icon: FileText, required: false },
    { id: 'packagingPrintedPkg', name: 'Packaging Material Scan (Printed Packaging Materials)', description: 'Artwork scan for printed packaging materials', icon: FileText, required: false },
    { id: 'packagingPackaging', name: 'Packaging Material Scan (Packaging)', description: 'Artwork scan for packaging materials', icon: FileText, required: false },
    { id: 'bulkCoa', name: 'Bulk Certificate of Analysis', description: 'CoA for bulk API materials', icon: FileText, required: false },
    { id: 'bulkCoc', name: 'Bulk Certificate of Conformance', description: 'CoC for bulk API materials', icon: FileText, required: false },
    { id: 'serialization', name: 'Serialization Report', description: 'Serialization data from TraceLink', icon: FileText, required: false },
    { id: 'aggregation', name: 'Aggregation Report', description: 'Aggregation hierarchy data', icon: FileText, required: false },
    { id: 'deviation', name: 'Deviation Report', description: 'Any deviations during manufacturing (if applicable)', icon: FileText, required: false },
    { id: 'other', name: 'Other', description: 'Any other supporting documentation', icon: FileText, required: false },
    { id: COMBINED_DOC_TYPE, name: 'Combined Document', description: 'Document containing multiple types (e.g. CoA + CoC)', icon: FileText, required: false },
  ];

  // Helper function to format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Helper function to detect document type from filename
  const detectDocumentType = (filename: string): string | null => {
    const lowerName = filename.toLowerCase();
    if (lowerName.includes('bulk') && lowerName.includes('coa')) return 'bulkCoa';
    if (lowerName.includes('bulk') && lowerName.includes('coc')) return 'bulkCoc';
    if (lowerName.includes('coc') || lowerName.includes('conformance')) return 'coc';
    if (lowerName.includes('coa') || lowerName.includes('analysis')) return 'coa';
    if (lowerName.includes('delivery') || lowerName.includes('packing') || lowerName.includes('invoice')) return 'delivery';
    if (lowerName.includes('bom') || lowerName.includes('bill of materials')) return 'bom';
    if (lowerName.includes('transport')) return 'transport';
    if (lowerName.includes('label') && (lowerName.includes('packaging') || lowerName.includes('kau') || lowerName.includes('artwork'))) return 'packagingLabel';
    if (lowerName.includes('foil') && (lowerName.includes('packaging') || lowerName.includes('kau') || lowerName.includes('artwork'))) return 'packagingFoil';
    if (lowerName.includes('leaflet')) return 'packagingLeaflet';
    if ((lowerName.includes('folding') || lowerName.includes('faltschachtel')) && (lowerName.includes('box') || lowerName.includes('packaging') || lowerName.includes('kau'))) return 'packagingFoldingBox';
    if (lowerName.includes('printed') && (lowerName.includes('packaging') || lowerName.includes('kau') || lowerName.includes('artwork'))) return 'packagingPrintedPkg';
    if (lowerName.includes('packaging') || lowerName.includes('kau')) return 'packagingPackaging';
    if (lowerName.includes('serialization')) return 'serialization';
    if (lowerName.includes('aggregation')) return 'aggregation';
    if (lowerName.includes('deviation')) return 'deviation';
    return null;
  };

  // Document handling functions
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    // Only allow drag if lots are selected (works for both search methods)
    if (!hasSelectedLots) {
      return;
    }
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFileUpload = useCallback(async (files: File[]) => {
    // Validate files
    const invalidFiles = files.filter(f => !f.name.toLowerCase().endsWith('.pdf'));
    if (invalidFiles.length > 0) {
      setUploadError(`Only PDF files are supported. Invalid files: ${invalidFiles.map(f => f.name).join(', ')}`);
      return;
    }

    const oversizedFiles = files.filter(f => f.size > 50 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setUploadError(`Files must be under 50MB. Oversized files: ${oversizedFiles.map(f => f.name).join(', ')}`);
      return;
    }

    if (!hasSelectedLots) {
      setUploadError('Please select an Inspection Lot before uploading documents');
      return;
    }

    setIsUploading(true);
    setUploadError('');
    setUploadInfo('');
    setUploadProgress(0);

    const errorMessages: string[] = [];

    try {
      // Capture file metadata before upload to ensure accurate size/name mapping
      const fileMetadata = new Map(
        files.map(file => [file.name, {
          size: file.size,
          name: file.name
        }])
      );

      const response = await apiClient.uploadDocuments(
        files,
        (_fileIndex: number, progress: number) => {
          setUploadProgress(progress);
        },
        activeSessionId || undefined
      );

      // Add successfully uploaded files to the list with correct metadata
      if (response.uploaded.length > 0) {
        const uploadTime = formatTime(new Date());
        const newDocs: Document[] = response.uploaded.map((uploadedFile: { filename: string; blob_name: string; url: string }, index: number) => {
          const metadata = fileMetadata.get(uploadedFile.filename);
          return {
            id: `${Date.now()}_${index}`,
            name: uploadedFile.filename,
            size: formatFileSize(metadata?.size || 0),
            uploadedAt: uploadTime,
            blobName: uploadedFile.blob_name,
            url: uploadedFile.url,
            documentType: detectDocumentType(uploadedFile.filename) || undefined,
            wasExtracted: false, // New uploads haven't been extracted yet
          };
        });
        setUploadedDocuments(prev => [...prev, ...newDocs]);

        // Show info about auto-detected document types
        const detectedNames = newDocs
          .filter(doc => doc.documentType)
          .map(doc => requiredDocuments.find(d => d.id === doc.documentType)?.name ?? doc.documentType);
        if (detectedNames.length > 0) {
          setUploadInfo(`Auto-detected: ${[...new Set(detectedNames)].join(', ')}`);
        }

        // Check for duplicate single-document types after this upload
        const allDocs = [...uploadedDocuments, ...newDocs];
        const getEffectiveTypes = (d: Document) => d.documentType === COMBINED_DOC_TYPE
          ? (d.documentTypes || []) : (d.documentType ? [d.documentType] : []);
        const coaCount = allDocs.filter(d => getEffectiveTypes(d).includes('coa')).length;
        const cocCount = allDocs.filter(d => getEffectiveTypes(d).includes('coc')).length;
        const duplicateWarningMessages: string[] = [];
        if (coaCount > 1) {
          duplicateWarningMessages.push('Multiple documents detected as CoA');
        }
        if (cocCount > 1) {
          duplicateWarningMessages.push('Multiple documents detected as CoC');
        }
        if (duplicateWarningMessages.length > 0) {
          errorMessages.push(`Warning: ${duplicateWarningMessages.join(' and ')}. Only one of each is allowed per batch. Please review and reassign document types.`);
        }
      }

      // Show errors if any files failed
      if (response.errors.length > 0) {
        const fileErrors = response.errors.map((e: { filename: string; error: string }) => `${e.filename}: ${e.error}`).join(', ');
        errorMessages.push(`Some files failed to upload: ${fileErrors}`);
      }

      if (errorMessages.length > 0) {
        setUploadError(errorMessages.join(' | '));
      }

    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error instanceof Error ? error.message : 'Failed to upload files');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [searchMethod, selectedLotId, selectedLotIds, userEmail, apiClient]);

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    // Only allow drop if lots are selected (works for both search methods)
    if (!hasSelectedLots) {
      return;
    }
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await handleFileUpload(files);
    }
  }, [hasSelectedLots, handleFileUpload]);

  const removeDocument = async (docId: string): Promise<void> => {
    // Find the document to get its blob name
    const doc = uploadedDocuments.find(d => d.id === docId);

    if (!doc) {
      console.error(`Document with id ${docId} not found`);
      return;
    }

    // If document has a blob name, delete from blob storage
    if (doc.blobName) {
      try {
        await apiClient.deleteBlobDocument(doc.blobName);
      } catch (error) {
        console.error(`Failed to delete ${doc.blobName} from blob storage:`, error);
        // Show error to user but still remove from UI
        setUploadError(`Warning: Failed to delete ${doc.name} from storage. ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    // Remove from local state regardless of blob deletion result
    setUploadedDocuments(prev => prev.filter(d => d.id !== docId));
  };

  const handleNewSession = async () => {
    setIsClearing(true);
    try {
      // Clean up all backend data for current session (best-effort, non-blocking)
      const cleanupPromises: Promise<void>[] = [];

      // 1. Delete uploaded documents from blob storage (brr-documents container)
      if (uploadedDocuments.length > 0) {
        cleanupPromises.push(
          apiClient.clearUserDocuments()
            .then(result => {
              if (result.errors.length > 0) {
                console.warn('[handleNewSession] Some document blobs failed to delete:', result.errors);
              }
            })
            .catch(error => {
              console.error('[handleNewSession] Failed to clear document blobs:', error);
            })
        );
      }

      // 2. Delete extraction results from blob storage (brr-results container)
      if (activeSessionId) {
        const sanitizedEmail = userEmail.replace('@', '_').replace(/[^a-zA-Z0-9._-]/g, '_');
        const blobPrefix = `${sanitizedEmail}/${activeSessionId}`;
        cleanupPromises.push(
          apiClient.cleanupBlobs(blobPrefix)
            .then(() => {
            })
            .catch(error => {
              console.warn('[handleNewSession] Failed to cleanup extraction results:', error);
            })
        );
      }

      // 3. Delete saved review from Neo4j
      if (batchNumber && activeSessionId) {
        cleanupPromises.push(
          apiClient.deleteBRR(batchNumber, activeSessionId)
            .then(() => {
            })
            .catch(error => {
              console.warn('[handleNewSession] Failed to delete Neo4j data:', error);
            })
        );
      }

      // Wait for all cleanup to complete (but don't block on failures)
      await Promise.allSettled(cleanupPromises);

      // Set flag to prevent stale lotInfoData from re-populating fields
      // Flag is cleared when user actively selects a NEW lot (detected by prevSelectedLotIdRef)
      justClearedRef.current = true;
      // Track that selectedLotId will be set to null, so we can detect when user selects a new lot
      prevSelectedLotIdRef.current = null;

      // Clear all form state
      setUploadedDocuments([]);
      setSavedDocumentCounts({});
      setBatchNumber('');
      setBatchSearchText('');
      setProduct('');
      setProductSearchText('');
      setProductCandidates([]);
      setSelectedProductDetail(null);
      setSelectedProductUid(null);
      setShowPreviousMatches(false);
      setProductTypeFilter('all');
      setDestinationCountry('');
      setCountrySearchText('');
      setInspectionLots([]);
      setInspectionSearchText('');
      setSelectedLotId(null);
      setMaterialDescription('');
      setPriority('medium');
      setConfirmationChecked(false);
      setDelayReason('');
      setDelayDetails('');
      setDelayActions('');
      setDelayCmoName('');
      setExpectedReleaseDate('');
      setSearchMethod('inspection-lot');
      setSelectedLotIds([]);
      setBatchIdSearchText('');
      setBatchIdConfirmed(false);
      setFieldSearchText('');
      setMaterialSearchText('');
      setSelectedMaterialId(null);
      setSelectedVendorBatchId(null);
      setMaterialId(null);
      setVendorBatchId(null);
      setBatchType('direct');
      setIndirectTransportLotId(null);
      setIndirectLotSearchText('');
      setBrrLevel(null);
      setSelectedPMP(null);
      setPmpSearchText('');
      setPendingPmpId(null);
      setPreviewBlobName(null);
      setUploadError('');
      setUploadInfo('');
      setClonedFromSessionId(null);
      setConflictCheck(null);
      setExistingReviews([]);

      // Generate a new session ID immediately (same pattern as session init effect)
      let newSessionId: string;
      try {
        const tempBatchId = 'NEW-' + Date.now();
        const session = await sessionClient.createSession({
          batchId: tempBatchId,
          currentPage: 'start-brr',
        });
        newSessionId = session.id;
      } catch (error) {
        console.error('[StartBRRPage] Session creation failed:', error);
        newSessionId = `fallback-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      }

      setActiveSessionId(newSessionId);
      // Clear batch state but notify parent of new session
      onBatchIdChange?.('');
      onSessionCreated?.(newSessionId);
    } finally {
      setIsClearing(false);
    }
  };

  const handleSaveProgress = async (): Promise<boolean> => {
    // Validate required fields before attempting save
    const missingFields: string[] = [];
    if (!batchNumber) missingFields.push('batchId');
    if (!activeSessionId) missingFields.push('sessionId');

    if (missingFields.length > 0) {
      const errorMsg = `Cannot save: missing ${missingFields.join(', ')}`;
      console.error('[StartBRRPage] Save validation failed:', errorMsg);
      setSaveError(errorMsg);
      return false;
    }

    if (delayReason === 'missing_cmo_documents' && !delayCmoName.trim()) {
      setSaveError('CMO name is required when delay reason is "Missing CMO Documents"');
      return false;
    }

    // Block navigation while saving
    blockNavigation?.('Saving Start BRR Page...');
    setIsSaving(true);
    setSaveError('');

    try {
      // Build the save payload
      const savePayload = {
        batchId: batchNumber,
        sessionId: activeSessionId,
        pageName: 'start_page' as const,
        pageData: {
          searchMethod,
          inspectionLotId: selectedLotId,
          selectedLotIds,
          batchIdSearchText,
          fieldSearchText,
          materialSearchText,
          selectedMaterialId,
          selectedVendorBatchId,
          materialId,
          vendorBatchId,
          productName: product,
          productUid: selectedProductUid,
          destinationCountry,
          materialDescription,
          indirectTransportLotId,
          uploadedDocuments,
          confirmationChecked,
          brrLevel,
          packagedMedicinalProduct: selectedPMP ? { id: selectedPMP.id, name: selectedPMP.name } : null,
          delayReason: delayReason || null,
          delayDetails: delayDetails || null,
          delayActions: delayActions || null,
          delayCmoName: delayCmoName || null,
          expectedReleaseDate: expectedReleaseDate || null,
        },
        batchType,
        priority,
        status: 'draft' as const,
      };

      // Save to Neo4j using new nested structure
      const response = await apiClient.saveReview(savePayload);

      // Display backend validation warnings if any
      if (response.warnings && response.warnings.length > 0) {
        console.warn('[StartBRRPage] Backend validation warnings:', response.warnings);
        setSaveError(`Warning: ${response.warnings.join('; ')}`);
      }

      // Log save action to the session audit log
      try {
        await sessionClient.logSave(
          batchNumber,
          activeSessionId,
          'start' // Page where save occurred
        );
      } catch (auditError) {
        console.error('[StartBRRPage] Audit log FAILED:', auditError);
        console.warn('Audit log failed, but save succeeded:', auditError);
      }

      // Update session metadata
      // IMPORTANT: Include details JSON with selectedDocuments so that when the user
      // resumes this session, DocumentValidationPage knows which document types to show.
      // Without this, newly added documents would be filtered out on resume.
      try {
        await sessionClient.updateSession(activeSessionId, {
          batchId: batchNumber, // Update from TEMP- to real batch ID
          currentPage: 'start-brr',
          lastSavedAt: new Date(),
          status: SessionStatus.InProgress,
          details: JSON.stringify({
            inspectionLotId: selectedLotId,
            materialId: materialId,
            vendorBatchId: vendorBatchId,
            productName: product,
            productUid: selectedProductUid ?? null,
            batchType: batchType,
            priority: priority,
            selectedDocuments: selectedDocumentIds,
            brrLevel: brrLevel ?? 'na',
            documentCounts: documentCounts,
            delayReason: delayReason || null,
            delayDetails: delayDetails || null,
            delayActions: delayActions || null,
            delayCmoName: delayCmoName || null,
            expectedReleaseDate: expectedReleaseDate || null,
          }),
        });
      } catch (sessionError) {
        // Session store failure is not critical - data is saved in Neo4j
        console.error('[StartBRRPage] session updateSession FAILED:', sessionError);
        console.warn('Session store save failed, but Neo4j save succeeded:', sessionError);
        if (!response.warnings || response.warnings.length === 0) {
          setSaveError('Warning: Session metadata save failed, but progress is saved.');
        }
      }

      setLastSaved(new Date());
      unblockNavigation?.();
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setSaveError(`Failed to save progress: ${errorMessage}`);
      console.error('Save progress failed:', error);
      unblockNavigation?.(`Save failed: ${errorMessage}`);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartBRR = async () => {
    // Validate every uploaded document has a type assigned
    const docsWithoutType = uploadedDocuments.filter(d => !d.documentType);
    if (docsWithoutType.length > 0) {
      const names = docsWithoutType.map(d => d.name).join(', ');
      setUploadError(`Please select a document type for: ${names}`);
      return;
    }

    // Validate at least one document has a type assigned
    if (selectedDocumentIds.length === 0) {
      setUploadError('Please assign a document type to at least one uploaded file before starting the BRR process');
      return;
    }

    // Block if any combined document has no sub-types selected
    const incompleteCombined = uploadedDocuments.some(
      d => d.documentType === COMBINED_DOC_TYPE && (!d.documentTypes || d.documentTypes.length === 0)
    );
    if (incompleteCombined) {
      setUploadError('Please select at least one document type for each Combined Document');
      return;
    }

    // Block navigation if there are duplicate CoA/CoC documents
    if (duplicateWarnings.length > 0) {
      setUploadError('Cannot proceed: ' + duplicateWarnings.join(' '));
      return;
    }

    if (confirmationChecked && hasSelectedLots) {
      // Auto-save before navigation (handleSaveProgress blocks navigation internally)
      const saveSuccess = await handleSaveProgress();
      if (!saveSuccess) {
        // Error notification already shown by handleSaveProgress via unblockNavigation
        setSaveError('Cannot proceed: Save failed. Please try again.');
        return;
      }

      const lotIdToUse = searchMethod !== 'inspection-lot' && selectedLotIds.length > 0 ? selectedLotIds[0] : selectedLotId;
      const docTypeMap: Record<string, string | string[]> = {};
      uploadedDocuments.forEach(d => {
        if (!d.blobName) return;
        if (d.documentType === COMBINED_DOC_TYPE && d.documentTypes && d.documentTypes.length > 0) {
          const backendTypes = d.documentTypes
            .map(dt => FRONTEND_TO_BACKEND_DOC_TYPE[dt])
            .filter(Boolean);
          if (backendTypes.length > 0) docTypeMap[d.blobName] = backendTypes;
        } else if (d.documentType && d.documentType !== COMBINED_DOC_TYPE) {
          const backendType = FRONTEND_TO_BACKEND_DOC_TYPE[d.documentType];
          if (backendType) docTypeMap[d.blobName] = backendType;
        }
      });
      // Pass documentCounts (ALL documents) for display in DocumentValidationPage.
      // Pass savedDocumentCounts (only Neo4j docs) as existingDocumentCounts for the
      // orchestrator to correctly index new documents without gaps.
      // Derive alreadyExtractedBlobs from documents with wasExtracted: true.

      const extractedDocs = uploadedDocuments.filter(d => d.wasExtracted === true);
      const alreadyExtractedBlobs = extractedDocs
        .map(d => d.blobName)
        .filter((name): name is string => !!name);
      // Convert savedDocumentCounts from frontend keys to backend keys.
      // The backend load_extraction_results expects backend keys (e.g., "PACKAGING_MATERIALS")
      // but savedDocumentCounts uses frontend keys (e.g., "packaging").
      const backendDocumentCounts: DocumentCounts = {};
      Object.entries(savedDocumentCounts).forEach(([frontendKey, count]) => {
        const backendKey = FRONTEND_TO_BACKEND_DOC_TYPE[frontendKey];
        if (backendKey) {
          backendDocumentCounts[backendKey] = count;
        }
      });

      await onStart({
        batchId: batchNumber,
        lotId: lotIdToUse,
        selectedLotIds,
        productName: product,
        batchType,
        indirectTransportLotId,
        priority,
        selectedDocuments: selectedDocumentIds,
        brrLevel: brrLevel ?? 'level1',
        documentCounts,
        existingDocumentCounts: backendDocumentCounts,
        productUid: selectedProductUid,
        documentTypeMap: docTypeMap,
        destinationCountry,
        delayReason: delayReason || null,
        delayDetails: delayDetails || null,
        delayActions: delayActions || null,
        delayCmoName: delayCmoName || null,
        expectedReleaseDate: expectedReleaseDate || null,
        alreadyExtractedBlobs,
      });
    }
  };

  const untypedDocuments = uploadedDocuments.filter(d => !d.documentType);
  const hasUntypedDocuments = untypedDocuments.length > 0;

  const canStartBRR = confirmationChecked && hasSelectedLots && !isSaving && !isNavigationBlocked && !hasBlockingConflict && !isCheckingConflict && (!!expectedReleaseDate || !!delayReason) && !(delayReason === 'missing_cmo_documents' && !delayCmoName.trim()) && !hasUntypedDocuments;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Start Batch Record Review</h1>
          <p className="text-sm text-gray-500">Upload batch documentation to begin the review process</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleNewSession}
            disabled={isClearing}
            onMouseDown={() => setIsNewPressed(true)}
            onMouseUp={() => setIsNewPressed(false)}
            onMouseLeave={() => setIsNewPressed(false)}
            onTouchStart={() => setIsNewPressed(true)}
            onTouchEnd={() => setIsNewPressed(false)}
            className="px-3 py-2 bg-white border border-gray-200 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            style={{
              transform: isNewPressed && !isClearing ? 'scale(0.95)' : 'scale(1)',
              transition: 'transform 100ms ease',
            }}
          >
            {isClearing ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            {isClearing ? 'Clearing...' : 'New'}
          </button>
        </div>
      </div>

      {/* Batch Selection */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <h3 className="font-semibold text-gray-900 mb-4">Batch Information</h3>

        {/* BRR Level Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">BRR Level</label>
          <div className="flex gap-4">
            {([
              { value: 'level1' as BRRLevel, label: 'Level 1' },
              { value: 'level2' as BRRLevel, label: 'Level 2' },
              { value: 'na' as BRRLevel, label: 'Not Applicable' },
            ]).map((option) => (
              <label key={option.value} className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="brrLevel"
                  value={option.value}
                  checked={brrLevel === option.value}
                  onChange={(e) => setBrrLevel(e.target.value as BRRLevel)}
                  className="text-green-600 focus:ring-green-500"
                />
                <span className="text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
          {brrLevel === null && (
            <p className="text-sm text-amber-600 mt-2">Select a BRR Level to begin</p>
          )}
        </div>

        <div className={brrLevel === null ? 'opacity-50 pointer-events-none' : ''}>
        {/* Batch Type Selection */}
        <div className="mb-4">
          <label htmlFor="batchType" className="block text-sm font-medium text-gray-700 mb-2">
            Batch Type
          </label>
          <select
            id="batchType"
            value={batchType}
            onChange={(e) => {
              const newBatchType = e.target.value as 'direct' | 'indirect';
              setBatchType(newBatchType);
              onBatchTypeChange?.(newBatchType);
              if (newBatchType === 'direct') {
                setIndirectTransportLotId(null);
                setIndirectLotSearchText('');
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="direct">Direct Transport</option>
            <option value="indirect">Indirect Transport</option>
          </select>
        </div>

        {/* Search Method Toggle */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search Method
          </label>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => {
                setSearchMethod('inspection-lot');
                setBatchIdSearchText('');
                setBatchIdConfirmed(false);
                setSelectedLotIds([]);
                setFieldSearchText('');
                setMaterialSearchText('');
                setSelectedMaterialId(null);
                setSelectedVendorBatchId(null);
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                searchMethod === 'inspection-lot'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Search by Inspection Lot
            </button>
            <button
              onClick={() => {
                setSearchMethod('batch-id');
                setInspectionSearchText('');
                setSelectedLotId(null);
                setFieldSearchText('');
                setMaterialSearchText('');
                setSelectedMaterialId(null);
                setSelectedVendorBatchId(null);
                if (batchNumber) {
                  setBatchIdSearchText(batchNumber);
                }
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                searchMethod === 'batch-id'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Search by SAP Internal Batch Number
            </button>
            <button
              onClick={() => {
                setSearchMethod('material-id');
                setInspectionSearchText('');
                setSelectedLotId(null);
                setBatchIdSearchText('');
                setBatchIdConfirmed(false);
                setSelectedLotIds([]);
                setFieldSearchText('');
                setMaterialSearchText('');
                setSelectedMaterialId(null);
                setSelectedVendorBatchId(null);
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                searchMethod === 'material-id'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Search by Material Number
            </button>
            <button
              onClick={() => {
                setSearchMethod('vendor-batch-id');
                setInspectionSearchText('');
                setSelectedLotId(null);
                setBatchIdSearchText('');
                setBatchIdConfirmed(false);
                setSelectedLotIds([]);
                setFieldSearchText('');
                setMaterialSearchText('');
                setSelectedMaterialId(null);
                setSelectedVendorBatchId(null);
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                searchMethod === 'vendor-batch-id'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Search by Vendor Batch ID
            </button>
          </div>
        </div>

        {/* Search Fields - Conditional based on method */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          {searchMethod === 'inspection-lot' ? (
            <>
              {/* Inspection Lot Search */}
              <div>
                <SearchableDropdown
                  label="Inspection Lot"
                  value={inspectionSearchText}
                  onSearchChange={(searchText) => {
                    setInspectionSearchText(searchText);
                  }}
                  onChange={(lot: InspectionLot) => {
                    setInspectionSearchText(lot.id);
                    setSelectedLotId(lot.id);
                    setInspectionLots([lot]);
                  }}
                  options={inspectionLotOptions}
                  placeholder="Type at least 3 digits to search..."
                  filterFn={undefined}
                  renderOption={(lot) => (
                    <div className="font-medium text-gray-900">{lot.id}</div>
                  )}
                />
                {inspectionLotsLoading && (
                  <div className="text-xs text-gray-500 mt-1">Searching...</div>
                )}
                {inspectionLotsError && (
                  <div className="text-xs text-red-600 mt-1">Error searching inspection lots</div>
                )}
              </div>

              {/* SAP Internal Batch Number (read-only, auto-populated) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SAP Internal Batch Number
                </label>
                <input
                  type="text"
                  value={formatBatchId(batchNumber)}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                  placeholder="Auto-populated from inspection lot"
                />
              </div>
            </>
          ) : searchMethod === 'batch-id' ? (
            <>
              {/* Batch ID Search with Autocomplete */}
              <div className="col-span-2">
                <SearchableDropdown
                  label="SAP Internal Batch Number"
                  value={batchIdSearchText}
                  onSearchChange={(searchText) => {
                    setBatchIdSearchText(searchText);
                    setBatchIdConfirmed(false);
                  }}
                  onChange={(batch: { batchId: string }) => {
                    const formatted = formatBatchId(batch.batchId);
                    setBatchIdSearchText(formatted);
                    setBatchNumber(formatted);
                    setBatchSearchText(formatted);
                    setBatchIdConfirmed(true);
                  }}
                  options={batchSearchResults}
                  placeholder="Type at least 3 characters to search..."
                  filterFn={undefined}
                  renderOption={(batch) => (
                    <div className="font-medium text-gray-900">{formatBatchId(batch.batchId)}</div>
                  )}
                  getDisplayValue={(batch) => formatBatchId(batch.batchId)}
                />
                {batchSearchLoading && (
                  <div className="text-xs text-gray-500 mt-1">Searching batch IDs...</div>
                )}
                {batchSearchError && (
                  <div className="text-xs text-red-600 mt-1">Error searching batch IDs</div>
                )}
              </div>
            </>
          ) : searchMethod === 'material-id' ? (
            <>
              {/* Material Number Search (Step 1) */}
              <div className="col-span-2">
                <SearchableDropdown
                  label="Material Number"
                  value={materialSearchText}
                  onSearchChange={(searchText) => {
                    setMaterialSearchText(searchText);
                    setSelectedMaterialId(null);
                    setBatchIdSearchText('');
                    setBatchIdConfirmed(false);
                    setSelectedLotIds([]);
                  }}
                  onChange={(mat: { materialId: string; productName: string | null }) => {
                    setSelectedMaterialId(mat.materialId);
                    setMaterialSearchText(mat.materialId + (mat.productName ? ` — ${mat.productName}` : ''));
                    setBatchIdSearchText('');
                    setBatchIdConfirmed(false);
                    setSelectedLotIds([]);
                  }}
                  options={materialResults}
                  placeholder="Type at least 3 digits of material number..."
                  filterFn={undefined}
                  renderOption={(mat) => (
                    <div className="flex gap-2 items-baseline">
                      <span className="font-medium text-gray-900 flex-shrink-0">{mat.materialId}</span>
                      {mat.productName && (
                        <span className="text-gray-500 text-sm truncate">{mat.productName}</span>
                      )}
                    </div>
                  )}
                  getDisplayValue={(mat) => mat.materialId + (mat.productName ? ` — ${mat.productName}` : '')}
                />
                {materialLoading && (
                  <div className="text-xs text-gray-500 mt-1">Searching materials...</div>
                )}
                {materialError && (
                  <div className="text-xs text-red-600 mt-1">Error searching materials</div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Vendor Batch ID Search (Step 1) */}
              <div className="col-span-2">
                <SearchableDropdown
                  label="Vendor Batch ID"
                  value={fieldSearchText}
                  onSearchChange={(searchText) => {
                    setFieldSearchText(searchText);
                    setSelectedVendorBatchId(null);
                    setBatchIdSearchText('');
                    setBatchIdConfirmed(false);
                    setSelectedLotIds([]);
                  }}
                  onChange={(vb: { vendorBatchId: string }) => {
                    setFieldSearchText(vb.vendorBatchId);
                    setSelectedVendorBatchId(vb.vendorBatchId);
                    setBatchIdSearchText('');
                    setBatchIdConfirmed(false);
                    setSelectedLotIds([]);
                  }}
                  options={vendorBatchResults}
                  placeholder="Type at least 3 characters of vendor batch ID..."
                  filterFn={undefined}
                  renderOption={(vb) => (
                    <div className="font-medium text-gray-900">{vb.vendorBatchId}</div>
                  )}
                  getDisplayValue={(vb) => vb.vendorBatchId}
                />
                {vendorBatchLoading && (
                  <div className="text-xs text-gray-500 mt-1">Searching vendor batch IDs...</div>
                )}
                {vendorBatchError && (
                  <div className="text-xs text-red-600 mt-1">Error searching vendor batch IDs</div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Indirect Transport Inspection Lot - Only visible for indirect batch type */}
        {batchType === 'indirect' && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="mb-3">
              <div className="flex items-center gap-2 text-sm text-blue-700 mb-2">
                <AlertCircle size={16} />
                <span className="font-medium">Indirect Transport</span>
              </div>
              <p className="text-xs text-blue-600">
                You may continue the Batch Record Review process and add this information later if needed.
              </p>
            </div>

            <SearchableDropdown
              label="Indirect Transport Inspection Lot (can be added later)"
              value={indirectLotSearchText}
              onSearchChange={(searchText) => setIndirectLotSearchText(searchText)}
              onChange={(lot: InspectionLot) => {
                setIndirectLotSearchText(lot.id);
                setIndirectTransportLotId(lot.id);
              }}
              options={indirectLotResults.map(result => ({
                id: result.lotId,
                batchId: '',
                status: ''
              }))}
              placeholder="Type at least 3 digits to search..."
              renderOption={(lot) => (
                <div className="font-medium text-gray-900">{lot.id}</div>
              )}
            />
            {indirectLotLoading && (
              <div className="text-xs text-gray-500 mt-1">Searching...</div>
            )}
            {indirectLotError && (
              <div className="text-xs text-red-600 mt-1">Error searching indirect transport lots</div>
            )}
          </div>
        )}

        {/* Batch Selection for Vendor Batch ID Search (Step 2) */}
        {searchMethod === 'vendor-batch-id' && selectedVendorBatchId && !vendorBatchBatchLoading && vendorBatchBatchResults.length > 0 && (
          batchIdConfirmed ? (
            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-600" />
                <span className="text-sm font-medium text-gray-700">Batch:</span>
                <span className="text-sm text-gray-900">{batchIdSearchText}</span>
              </div>
              <button
                onClick={() => {
                  setBatchIdConfirmed(false);
                  setSelectedLotIds([]);
                }}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Batch ({vendorBatchBatchResults.length} available)
              </label>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {vendorBatchBatchResults.map((batch) => (
                  <label
                    key={batch.batchId}
                    className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer transition-colors"
                  >
                    <input
                      type="radio"
                      name="vendorBatchBatchSelect"
                      checked={batchIdSearchText === formatBatchId(batch.batchId)}
                      onChange={() => {
                        const formatted = formatBatchId(batch.batchId);
                        setBatchIdSearchText(formatted);
                        setBatchNumber(formatted);
                        setBatchSearchText(formatted);
                        setBatchIdConfirmed(true);
                        setSelectedLotIds([]);
                      }}
                      className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-900">{formatBatchId(batch.batchId)}</span>
                  </label>
                ))}
              </div>
            </div>
          )
        )}
        {searchMethod === 'vendor-batch-id' && selectedVendorBatchId && vendorBatchBatchLoading && (
          <div className="mb-4 text-xs text-gray-500">Loading batches for vendor batch {selectedVendorBatchId}...</div>
        )}
        {searchMethod === 'vendor-batch-id' && selectedVendorBatchId && !vendorBatchBatchLoading && vendorBatchBatchResults.length === 0 && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-700">
                <span className="font-medium">No batches found</span> for vendor batch: {selectedVendorBatchId}
              </div>
            </div>
          </div>
        )}

        {/* Batch Selection for Material Search (Step 2) */}
        {searchMethod === 'material-id' && selectedMaterialId && !materialBatchLoading && materialBatchResults.length > 0 && (
          batchIdConfirmed ? (
            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-600" />
                <span className="text-sm font-medium text-gray-700">Batch:</span>
                <span className="text-sm text-gray-900">{batchIdSearchText}</span>
              </div>
              <button
                onClick={() => {
                  setBatchIdConfirmed(false);
                  setSelectedLotIds([]);
                }}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Batch ({materialBatchResults.length} available)
              </label>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {materialBatchResults.map((batch) => (
                  <label
                    key={batch.batchId}
                    className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer transition-colors"
                  >
                    <input
                      type="radio"
                      name="materialBatchSelect"
                      checked={batchIdSearchText === formatBatchId(batch.batchId)}
                      onChange={() => {
                        const formatted = formatBatchId(batch.batchId);
                        setBatchIdSearchText(formatted);
                        setBatchNumber(formatted);
                        setBatchSearchText(formatted);
                        setBatchIdConfirmed(true);
                        setSelectedLotIds([]);
                      }}
                      className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-900">{formatBatchId(batch.batchId)}</span>
                  </label>
                ))}
              </div>
            </div>
          )
        )}
        {searchMethod === 'material-id' && selectedMaterialId && materialBatchLoading && (
          <div className="mb-4 text-xs text-gray-500">Loading batches for material {selectedMaterialId}...</div>
        )}
        {searchMethod === 'material-id' && selectedMaterialId && !materialBatchLoading && materialBatchResults.length === 0 && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-700">
                <span className="font-medium">No batches found</span> for material: {selectedMaterialId}
              </div>
            </div>
          </div>
        )}

        {/* Inspection Lot Selection (only after user confirms a batch ID) */}
        {searchMethod !== 'inspection-lot' && batchIdConfirmed && batchIdResults.length > 0 && (
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                {selectedLotIds.length > 0
                  ? `${selectedLotIds.length} Inspection Lot${selectedLotIds.length > 1 ? 's' : ''} Selected`
                  : `Select Inspection Lots (${batchIdResults.length} available)`}
              </label>
              {selectedLotIds.length > 0 && (
                <button
                  onClick={() => setSelectedLotIds([])}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear All
                </button>
              )}
            </div>
            {/* Show selected lots summary */}
            {selectedLotIds.length > 0 && (
              <div className="flex items-center gap-2 mb-2 p-2 bg-white rounded border border-gray-100">
                <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                <span className="text-sm text-gray-900">{selectedLotIds.join(', ')}</span>
              </div>
            )}
            {/* Warning when multiple lots selected */}
            {selectedLotIds.length > 1 && (
              <div className="flex items-start gap-2 mb-2 p-2 bg-amber-50 rounded border border-amber-200">
                <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-amber-700">
                  Multiple lots selected. Product and country data is populated from the first lot ({selectedLotIds[0]}). Verify this matches all selected lots.
                </span>
              </div>
            )}
            {/* Checkbox list */}
            <div className="max-h-48 overflow-y-auto space-y-1">
              {batchIdResults.map((lot) => (
                <label
                  key={lot.lotId}
                  className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedLotIds.includes(lot.lotId)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedLotIds([...selectedLotIds, lot.lotId]);
                      } else {
                        setSelectedLotIds(selectedLotIds.filter(id => id !== lot.lotId));
                      }
                    }}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-900">{lot.lotId}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* No Results Message (only after user confirms a batch ID) */}
        {searchMethod !== 'inspection-lot' &&
         batchIdConfirmed &&
         batchIdSearchText.length >= 3 &&
         !batchIdLoading &&
         batchIdResults.length === 0 && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-700">
                <span className="font-medium">No inspection lots found</span> for batch: {batchIdSearchText}
              </div>
            </div>
          </div>
        )}

        {/* Lot info loading/error indicators */}
        {lotInfoLoading && (
          <div className="text-xs text-gray-500 mt-2">Loading batch information...</div>
        )}
        {lotInfoError && (
          <div className="text-xs text-red-600 mt-2">Error loading lot info: {lotInfoError.message}</div>
        )}

        {/* Product — full-width searchable dropdown */}
        <div className="mt-4">
          <SearchableDropdown<EnrichedProduct | string>
            label={lotInfoData?.productType === 'pharmaceutical' ? 'Pharmaceutical Product' : 'Product'}
            value={productSearchText}
            onChange={(prod: EnrichedProduct | string) => {
              if (typeof prod === 'string') {
                if (prod === LOAD_MORE_SENTINEL) {
                  productSearchLoadMore();
                  return;
                }
                setProduct(prod);
                setProductSearchText(prod);
                setSelectedProductDetail(null);
                setSelectedProductUid(null);
              } else {
                setProduct(prod.name);
                setProductSearchText(prod.name);
                setSelectedProductDetail(prod);
                setSelectedProductUid(prod.uid);
              }
            }}
            onSearchChange={(text) => setProductSearchText(text)}
            options={productOptions}
            placeholder={productSearchLoading ? "Searching products..." : ""}
            getDisplayValue={(opt) => typeof opt === 'string' ? opt : opt.name}
            renderOption={(opt) => {
              if (typeof opt === 'string') {
                if (opt === LOAD_MORE_SENTINEL) {
                  return (
                    <div className="text-center py-1">
                      {filterEmptyButMoreAvailable && (
                        <div className="text-xs text-gray-500 mb-1">No {productTypeFilter === 'pharmaceutical' ? 'Pharmaceutical' : 'Packaged'} products in current results</div>
                      )}
                      <div className="text-sm text-blue-600 font-medium">
                        {productSearchLoadingMore ? 'Loading...' : 'Load More Results'}
                      </div>
                    </div>
                  );
                }
                return <span>{opt}</span>;
              }
              const typeLabel = opt.productType === 'Pharmaceutical Product' ? 'Pharmaceutical' : 'Packaged';
              const typeColor = opt.productType === 'Pharmaceutical Product' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800';
              return (
                <div>
                  <div className="flex items-center gap-2">
                    <span>{opt.name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${typeColor}`}>{typeLabel}</span>
                  </div>
                  {(opt.doseForm || opt.doseStrength) && (
                    <div className="text-xs text-gray-400">
                      {[opt.doseForm, opt.doseStrength].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
              );
            }}
            filterFn={() => {
              // API search results are already filtered server-side
              return true;
            }}
            shouldCloseOnSelect={(opt) => typeof opt === 'string' && opt === LOAD_MORE_SENTINEL ? false : true}
            dropdownHeader={productSearchResults.length > 0 ? (
              <div className="sticky top-0 bg-white z-10 px-3 py-2 border-b border-gray-200 flex items-center gap-1">
                {(['all', 'packaged', 'pharmaceutical'] as const).map(filter => {
                  const isActive = productTypeFilter === filter;
                  const label = filter === 'all' ? 'All' : filter === 'packaged' ? 'Packaged' : 'Pharmaceutical';
                  return (
                    <button
                      key={filter}
                      type="button"
                      onClick={() => setProductTypeFilter(filter)}
                      className={`text-xs px-2 py-0.5 rounded-full font-medium transition-colors ${
                        isActive
                          ? 'bg-gray-800 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            ) : undefined}
          />
          {selectedLotId && !lotInfoLoading && !selectedProductDetail && lotInfoData?.matchType === 'candidates' && (
            <p className="mt-1.5 text-sm text-amber-600">
              Candidate matches found. Select one below, or type above to search.
            </p>
          )}
          {selectedLotId && !lotInfoLoading && !selectedProductDetail && lotInfoData?.matchType === null && (
            <p className="mt-1.5 text-sm text-amber-600">
              No matching CARA product found. Type above to search and select a product manually.
            </p>
          )}
        </div>

        {productCandidates.length > 0 && lotInfoData?.matchType !== null && (
          <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-700">
                Please select a product from the list below before proceeding to document extraction.
              </div>
            </div>
          </div>
        )}

        {/* Other Matches toggle — show when there are alternative matches (candidates, confirmed, or exact with extras) */}
        {productCandidates.length > 0 && lotInfoData?.matchType !== null && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setShowPreviousMatches(!showPreviousMatches)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              {showPreviousMatches ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              Show Other Matches ({productCandidates.length})
            </button>
            {showPreviousMatches && (
              <div className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100">
                {[...productCandidates].sort((a, b) => {
                  // Priority: exact AI matches first, then confirmed, then candidates
                  const rank = (p: EnrichedProduct) =>
                    p.matchSource === 'exact' && !p.userSelected ? 0
                    : p.userSelected ? 1
                    : 2;
                  return rank(a) - rank(b);
                }).map((p, idx) => {
                  const isUser = p.userSelected === true;
                  const matchLabel = isUser ? 'Confirmed' : 'AI Match';
                  const matchColor = isUser ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800';
                  const conf = p.matchConfidence;
                  const confLabel = conf != null ? (conf >= 0.8 ? 'High' : conf >= 0.5 ? 'Medium' : 'Low') : null;
                  const confColor = confLabel === 'High' ? 'bg-green-100 text-green-700'
                    : confLabel === 'Medium' ? 'bg-amber-100 text-amber-700'
                    : confLabel === 'Low' ? 'bg-red-100 text-red-700' : '';
                  return (
                    <button
                      key={p.uid || idx}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between"
                      onClick={() => {
                        setProduct(p.name);
                        setProductSearchText(p.name);
                        setSelectedProductDetail(p);
                        setSelectedProductUid(p.uid);
                        setShowPreviousMatches(false);
                      }}
                    >
                      <div>
                        <div className="text-sm font-medium text-gray-900">{p.name}</div>
                        {(p.doseForm || p.doseStrength) && (
                          <div className="text-xs text-gray-500">
                            {[p.doseForm, p.doseStrength].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${matchColor}`}>
                          {matchLabel}
                        </span>
                        {confLabel && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${confColor}`}>
                            {confLabel}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* CARA/SAP comparison panel — only show when a product is selected */}
        {selectedLotId && selectedProductDetail && (
          <ProductComparisonPanel
            caraProduct={selectedProductDetail}
            sapMaterial={lotInfoData?.sapMaterial ?? null}
            matchType={lotInfoData?.matchType ?? null}
            isLoading={lotInfoLoading}
          />
        )}

        {/* Packaged Medicinal Product dropdown — Level 2 only */}
        {brrLevel === 'level2' && lotInfoData?.productType === 'pharmaceutical' && product && (
          <div className="mt-4">
            <SearchableDropdown<PackagedProduct>
              label="Packaged Medicinal Product"
              value={pmpSearchText}
              onChange={(pmp: PackagedProduct) => {
                setSelectedPMP(pmp);
                setPmpSearchText(pmp.name);
              }}
              onSearchChange={(text: string) => setPmpSearchText(text)}
              options={packagedProducts || []}
              placeholder={pmpLoading ? 'Loading...' : ''}
              renderOption={(pmp: PackagedProduct) => (
                <div>
                  <span className="font-medium">{pmp.name}</span>
                  {pmp.markets[0] && (
                    <span className="text-xs text-gray-500 ml-2">
                      ({pmp.markets[0].countryName})
                    </span>
                  )}
                </div>
              )}
              filterFn={(pmp: PackagedProduct, text: string) =>
                pmp.name.toLowerCase().includes(text.toLowerCase()) ||
                pmp.markets.some(m => m.countryName.toLowerCase().includes(text.toLowerCase()))
              }
              getDisplayValue={(pmp: PackagedProduct) => pmp.name}
            />
            {pmpLoading && (
              <div className="text-xs text-gray-500 mt-1">Loading packaged products...</div>
            )}
            {pmpError && (
              <div className="text-xs text-amber-600 mt-1">No packaged products found</div>
            )}
          </div>
        )}

        {/* Destination Country and Material Description */}
        <div className="grid grid-cols-2 gap-4 mt-4">
          <SearchableDropdown
            label="Destination Country"
            value={countrySearchText}
            onChange={(country: string) => {
              setDestinationCountry(country);
              setCountrySearchText(country);
            }}
            options={availableCountries}
            placeholder=""
            filterFn={(country, searchText) => country.toLowerCase().includes(searchText.toLowerCase())}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Material Description
            </label>
            <input
              type="text"
              value={materialDescription}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
              placeholder="Auto-populated from inspection lot"
            />
          </div>
        </div>

        {/* Priority Selection */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Priority
          </label>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value as 'low' | 'medium' | 'high')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="low">Low Priority</option>
            <option value="medium">Medium Priority</option>
            <option value="high">High Priority</option>
          </select>
          {priority === 'high' && (
            <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm text-orange-800 font-medium">
                Batch Record Review must be complete within 48 hours.
              </p>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Delay Reporting Section */}
      <div className={`bg-white rounded-lg border border-gray-200 shadow-sm p-6 space-y-4${!hasSelectedLots ? ' opacity-50 pointer-events-none' : ''}`}>
        <div>
          <h2 className="text-base font-semibold text-gray-900">Delay Reporting</h2>
          <p className="text-sm text-gray-500">
            {hasSelectedLots
              ? 'Report if this batch review cannot proceed'
              : 'Select an inspection lot first'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Delay Reason
          </label>
          <select
            value={delayReason}
            onChange={(e) => {
              const val = e.target.value as DelayReason | '';
              setDelayReason(val);
              if (!val) { setDelayDetails(''); setDelayActions(''); setDelayCmoName(''); }
              if (val !== 'missing_cmo_documents') { setDelayCmoName(''); }
            }}
            disabled={!hasSelectedLots}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
              !hasSelectedLots ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''
            }`}
          >
            <option value="">No delay</option>
            <option value="no_inspection_lot">No Inspection Lot in SAP</option>
            <option value="missing_cmo_documents">Missing CMO Documents</option>
            <option value="missing_serialisation">Missing Serialisation</option>
            <option value="other">Other</option>
          </select>
        </div>

        {delayReason === 'missing_cmo_documents' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CMO Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={delayCmoName}
              onChange={(e) => setDelayCmoName(e.target.value)}
              placeholder="Enter the name of the CMO with missing documents"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                !delayCmoName.trim() ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {!delayCmoName.trim() && (
              <p className="mt-1 text-sm text-red-600">CMO name is required when delay is due to missing CMO documents</p>
            )}
          </div>
        )}

        {delayReason && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Delay Details {delayReason === 'other' && <span className="text-red-500">*</span>}
              </label>
              <textarea
                value={delayDetails}
                onChange={(e) => setDelayDetails(e.target.value)}
                placeholder="Provide additional details about the delay..."
                rows={3}
                style={{ border: delayReason === 'other' && !delayDetails.trim() ? '1px solid #fca5a5' : '1px solid #d1d5db' }}
                className={`w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none ${
                  delayReason === 'other' && !delayDetails.trim() ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {delayReason === 'other' && !delayDetails.trim() && (
                <p className="mt-1 text-sm text-red-600">Details are required when delay reason is &quot;Other&quot;</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Actions Being Taken
              </label>
              <textarea
                value={delayActions}
                onChange={(e) => setDelayActions(e.target.value)}
                placeholder="Describe what actions are being taken to resolve the delay..."
                rows={3}
                style={{ border: '1px solid #d1d5db' }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
              />
            </div>
          </>
        )}
      </div>

      {/* Expected Release Date Section */}
      <div className={`bg-white rounded-lg border border-gray-200 shadow-sm p-6 space-y-4${!hasSelectedLots ? ' opacity-50 pointer-events-none' : ''}`}>
        <div>
          <h2 className="text-base font-semibold text-gray-900">Expected Release Date</h2>
          <p className="text-sm text-gray-500">
            {!hasSelectedLots
              ? 'Select an inspection lot first'
              : delayReason
                ? 'Optional when a delay has been reported'
                : 'Required before starting the batch review'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Expected Release Date {!delayReason && <span className="text-red-500">*</span>}
          </label>
          <input
            type="date"
            value={expectedReleaseDate}
            onChange={(e) => setExpectedReleaseDate(e.target.value)}
            disabled={!hasSelectedLots}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
              !hasSelectedLots ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-300' :
              !delayReason && !expectedReleaseDate ? 'border-amber-300' : 'border-gray-300'
            }`}
          />
        </div>
      </div>

      {/* Clone banner — shown when this BRR was created from a template */}
      {clonedFromSessionId && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
          <FileText size={16} className="text-blue-600 flex-shrink-0" />
          <span className="text-sm text-blue-800">
            This BRR was created from an existing review as a template.
          </span>
        </div>
      )}

      {/* BRR Templates for this material — clone as template */}
      {existingReviews.length > 0 && (
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
          <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <FileText size={18} />
            BRR Templates for Material {materialId} ({existingReviews.length})
          </h3>
          <p className="text-sm text-blue-700 mb-3">
            Previous BRRs for this material. Use one as a template to pre-fill your new review.
          </p>
          <div className="space-y-2">
            {existingReviews.map(review => {
              const isOwner = (review.userEmail || '').toLowerCase() === userEmail.toLowerCase();
              const isSameBatch = review.batchId === batchNumber;
              return (
                <div key={review.sessionId} className="bg-white rounded border border-blue-100 p-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {review.productName || 'No product'} — <span className={`${review.status === 'submitted' ? 'text-green-700' : 'text-amber-700'}`}>{review.status}</span>
                      {isSameBatch && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Same batch</span>}
                    </div>
                    <div className="text-xs text-gray-500">
                      Batch {formatBatchId(review.batchId)}
                      {review.inspectionLotId && <> · Lot: {review.inspectionLotId}</>}
                      {' '}· By {review.userEmail}
                      {' '}· {review.savedAt ? new Date(review.savedAt).toLocaleDateString() : 'N/A'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isSameBatch && onResumeSession && (
                      <button
                        onClick={() => onResumeSession(review.sessionId)}
                        className={`px-3 py-1.5 text-sm rounded ${
                          isOwner
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {isOwner ? 'Resume' : 'View'}
                      </button>
                    )}
                    <button
                      onClick={() => { setSelectedCloneSource(review); setShowCloneDialog(true); setCloneError(''); }}
                      className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                    >
                      Use as Template
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-6">
        {/* Document Upload Area */}
        <div className="col-span-2 space-y-4">
          {/* Drag and Drop Zone */}
          <div
            className={`bg-white rounded-lg border-2 border-dashed transition-all ${
              isDragging
                ? 'border-green-500 bg-green-50'
                : 'border-gray-300 hover:border-green-400'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="p-8 text-center">
              <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                isDragging ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                <Upload size={32} className={isDragging ? 'text-green-600' : 'text-gray-400'} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {isDragging ? 'Drop files here' : 'Drag and drop files'}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                or click to browse from your computer
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length > 0) {
                    void handleFileUpload(files);
                  }
                  // Reset input so same file can be selected again
                  e.target.value = '';
                }}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || !hasSelectedLots}
                className="px-4 py-2 bg-green-600 text-white rounded font-medium hover:bg-green-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isUploading ? 'Uploading...' : 'Browse Files'}
              </button>
              {!hasSelectedLots && (
                <p className="text-xs text-amber-600 mt-2">
                  {searchMethod === 'inspection-lot'
                    ? 'Please select an Inspection Lot before uploading documents'
                    : 'Please select at least one lot before uploading documents'}
                </p>
              )}
              <p className="text-xs text-gray-400 mt-4">
                Supported formats: PDF (Max 50MB per file)
              </p>
              {isUploading && (
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{uploadProgress}%</p>
                </div>
              )}
              {uploadError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                  {uploadError}
                </div>
              )}
              {uploadInfo && !uploadError && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-600 break-words">
                  {uploadInfo}
                </div>
              )}
            </div>
          </div>

          {/* Uploaded Documents List */}
          {uploadedDocuments.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Uploaded Documents ({uploadedDocuments.length})</h3>
                  {selectedDocumentIds.length > 0 && (
                    <span className="text-xs text-green-600 font-medium">
                      {selectedDocumentIds.length} type{selectedDocumentIds.length !== 1 ? 's' : ''} detected
                    </span>
                  )}
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {uploadedDocuments.map((doc) => {
                  // Check document status
                  const isAlreadyExtracted = doc.wasExtracted === true;
                  const isPlaceholder = doc.needsUpload === true;
                  return (
                  <div key={doc.id} className={`p-4 hover:bg-gray-50 transition-colors ${isPlaceholder ? 'border-l-4 border-blue-400 bg-blue-50' : ''}`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded flex items-center justify-center ${
                        isPlaceholder
                          ? 'bg-blue-100 border-2 border-dashed border-blue-300'
                          : isAlreadyExtracted
                            ? 'bg-green-100'
                            : 'bg-amber-100'
                      }`}>
                        {isPlaceholder ? (
                          <Upload size={20} className="text-blue-500" />
                        ) : (
                          <File size={20} className={isAlreadyExtracted ? 'text-green-500' : 'text-amber-500'} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium truncate ${isPlaceholder ? 'text-blue-900' : 'text-gray-900'}`}>
                            {isPlaceholder ? `${doc.documentType || 'Document'} (from template)` : doc.name}
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                            isPlaceholder
                              ? 'bg-blue-100 text-blue-700'
                              : isAlreadyExtracted
                                ? 'bg-green-100 text-green-700'
                                : 'bg-amber-100 text-amber-700'
                          }`}>
                            {isPlaceholder ? 'Needs Upload' : isAlreadyExtracted ? 'Extracted' : 'Not Extracted'}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          {isPlaceholder ? (
                            <span className="text-blue-600">Upload a document to replace this placeholder</span>
                          ) : (
                            <>
                              <span>{doc.size}</span>
                              <span>•</span>
                              <span>Uploaded at {doc.uploadedAt}</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Document Type Dropdown */}
                      <select
                        value={doc.documentType || ''}
                        onChange={(e) => {
                          const newType = e.target.value || undefined;
                          setUploadedDocuments(prev =>
                            prev.map(d => d.id === doc.id
                              ? { ...d, documentType: newType, documentTypes: newType === COMBINED_DOC_TYPE ? (d.documentTypes || []) : undefined }
                              : d
                            )
                          );
                        }}
                        className={`px-2 py-1.5 border rounded text-sm bg-white min-w-[180px] ${
                          (doc.documentType && doc.documentType !== COMBINED_DOC_TYPE) ||
                          (doc.documentType === COMBINED_DOC_TYPE && (doc.documentTypes || []).length > 0)
                            ? 'border-green-300 text-gray-900' : 'border-amber-300 text-gray-500'
                        }`}
                        data-testid={`doc-type-select-${doc.id}`}
                      >
                        <option value="">Select type...</option>
                        {requiredDocuments.map(rd => {
                          // Disable single-document types if already assigned to ANOTHER document
                          const isDisabled = rd.id !== COMBINED_DOC_TYPE &&
                            SINGLE_DOCUMENT_TYPES.includes(rd.id) &&
                            assignedSingleTypes.has(rd.id) &&
                            doc.documentType !== rd.id;
                          return (
                            <option key={rd.id} value={rd.id} disabled={isDisabled}>
                              {rd.name}{isDisabled ? ' (already assigned)' : ''}
                            </option>
                          );
                        })}
                      </select>

                      {/* Action Buttons */}
                      <div className="flex items-center gap-2">
                        {isPlaceholder ? (
                          <button
                            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center gap-1"
                            onClick={() => fileInputRef.current?.click()}
                            title="Upload document to replace placeholder"
                          >
                            <Upload size={14} />
                            Upload
                          </button>
                        ) : (
                          <button
                            className="p-1.5 hover:bg-gray-200 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Preview"
                            disabled={!doc.blobName}
                            onClick={() => doc.blobName && setPreviewBlobName(doc.blobName)}
                          >
                            <Eye size={16} className="text-gray-400" />
                          </button>
                        )}
                        <button
                          className="p-1.5 hover:bg-red-100 rounded transition-colors"
                          onClick={() => removeDocument(doc.id)}
                          title="Remove"
                        >
                          <Trash2 size={16} className="text-gray-400 hover:text-red-500" />
                        </button>
                      </div>
                    </div>

                    {/* Combined Document sub-type picker */}
                    {doc.documentType === COMBINED_DOC_TYPE && (
                      <div className="mt-2 ml-1" data-testid={`doc-combined-picker-${doc.id}`}>
                        {/* Selected sub-type tags */}
                        {(doc.documentTypes || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {(doc.documentTypes || []).map(dt => {
                              const rdName = requiredDocuments.find(r => r.id === dt)?.name || dt;
                              return (
                                <span
                                  key={dt}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"
                                >
                                  {rdName}
                                  <button
                                    onClick={() => setUploadedDocuments(prev => prev.map(d =>
                                      d.id === doc.id
                                        ? { ...d, documentTypes: (d.documentTypes || []).filter(t => t !== dt) }
                                        : d
                                    ))}
                                    className="hover:bg-green-200 rounded-full p-0.5"
                                    title={`Remove ${rdName}`}
                                  >
                                    <X size={10} />
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {/* Checkbox list of available types */}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 p-2 bg-gray-50 rounded border border-gray-200 max-h-48 overflow-y-auto">
                          {requiredDocuments
                            .filter(rd => rd.id !== COMBINED_DOC_TYPE && rd.id !== 'other')
                            .map(rd => {
                              const isChecked = (doc.documentTypes || []).includes(rd.id);
                              const isDisabledByOther = SINGLE_DOCUMENT_TYPES.includes(rd.id)
                                && assignedSingleTypes.has(rd.id)
                                && !isChecked;
                              return (
                                <label
                                  key={rd.id}
                                  className={`flex items-center gap-2 text-xs py-1 cursor-pointer ${
                                    isDisabledByOther ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'
                                  } rounded px-1`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    disabled={isDisabledByOther}
                                    onChange={() => {
                                      setUploadedDocuments(prev => prev.map(d => {
                                        if (d.id !== doc.id) return d;
                                        const current = d.documentTypes || [];
                                        const updated = current.includes(rd.id)
                                          ? current.filter(t => t !== rd.id)
                                          : [...current, rd.id];
                                        return { ...d, documentTypes: updated };
                                      }));
                                    }}
                                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                                    data-testid={`doc-combined-checkbox-${doc.id}-${rd.id}`}
                                  />
                                  <span className={isDisabledByOther ? 'text-gray-400' : 'text-gray-700'}>
                                    {rd.name}{isDisabledByOther ? ' (assigned)' : ''}
                                  </span>
                                </label>
                              );
                            })}
                        </div>
                        {(doc.documentTypes || []).length === 0 && (
                          <p className="text-xs text-amber-600 mt-1">Select at least one document type above</p>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>

              {/* Duplicate document type warning */}
              {duplicateWarnings.length > 0 && (
                <div className="p-4 bg-amber-50 border-t border-amber-200">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-700">
                      {duplicateWarnings.map((warning, idx) => (
                        <p key={idx} className={idx > 0 ? 'mt-1' : ''}>{warning}</p>
                      ))}
                      <p className="mt-2 text-amber-600 font-medium">Please reassign document types before proceeding.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <div className="space-y-4">
          {/* Action Buttons */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="space-y-3">
              {/* Confirmation Checkbox */}
              <label className="flex items-start gap-2.5 p-3 bg-green-50 border-2 border-green-200 rounded-lg cursor-pointer hover:bg-green-100 transition-colors">
                <input
                  type="checkbox"
                  checked={confirmationChecked}
                  onChange={(e) => setConfirmationChecked(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 flex-shrink-0"
                />
                <span className="text-sm text-gray-900 flex-1" style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                  I confirm all required documents have been input for the Batch Record Review Process
                </span>
              </label>

              {/* Save status indicator */}
              {lastSaved && (
                <div className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
                  <Clock size={14} className="flex-shrink-0" />
                  <span>Last saved: {formatTime(lastSaved)}</span>
                </div>
              )}

              {/* Save error */}
              {saveError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-700 flex-1" style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    {saveError}
                  </div>
                </div>
              )}

              {/* Save Progress button */}
              {ENABLE_SAVE_FUNCTIONALITY && (
                <button
                  onClick={() => void handleSaveProgress()}
                  disabled={isSaving || !batchNumber}
                  className={`w-full py-3 rounded font-medium transition-colors flex items-center justify-center gap-2 ${
                    isSaving || !batchNumber
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {isSaving ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FileText size={18} />
                      Save Progress
                    </>
                  )}
                </button>
              )}

              {/* Conflict warnings */}
              {hasBlockingConflict && conflictCheck?.exactMatch && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-700 flex-1" style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    <span className="font-medium">Cannot proceed:</span> A BRR already exists for this batch and inspection lot combination.
                    <br />
                    <span className="text-xs">Owned by: {conflictCheck.exactMatch.userEmail} ({conflictCheck.exactMatch.status})</span>
                    <button
                      onClick={() => setShowConflictModal(true)}
                      className="ml-2 text-xs text-red-600 underline hover:text-red-800"
                    >
                      View details
                    </button>
                  </div>
                </div>
              )}

              {conflictCheck?.partialMatches && conflictCheck.partialMatches.length > 0 && !hasBlockingConflict && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-700 flex-1" style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    <span className="font-medium">Warning:</span> Other BRRs exist with overlapping inspection lots for this batch.
                    <ul className="mt-1 text-xs list-disc list-inside">
                      {conflictCheck.partialMatches.map((match, idx) => (
                        <li key={idx}>
                          {match.userEmail} - lots: {match.overlappingLots.join(', ')} ({match.status})
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Expected release date validation message */}
              {!expectedReleaseDate && !delayReason && hasSelectedLots && confirmationChecked && !hasBlockingConflict && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-700 flex-1" style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    <span className="font-medium">Expected Release Date required:</span> Please set an expected release date or report a delay reason before starting.
                  </div>
                </div>
              )}

              {/* Start BRR button */}
              <button
                className={`w-full py-3 rounded font-medium transition-colors flex items-center justify-center gap-2 ${
                  canStartBRR
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
                disabled={!canStartBRR}
                onClick={handleStartBRR}
              >
                {isSaving || isNavigationBlocked || isCheckingConflict ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    {isSaving ? 'Saving...' : isCheckingConflict ? 'Checking...' : 'Please wait...'}
                  </>
                ) : (
                  <>
                    <PlayCircle size={18} />
                    Start Batch Record Review Process
                  </>
                )}
              </button>

              {hasUntypedDocuments && hasSelectedLots && !hasBlockingConflict && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-700 flex-1" style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    <span className="font-medium">Document type required:</span> {untypedDocuments.length} document{untypedDocuments.length !== 1 ? 's have' : ' has'} no type assigned. Please select a type for every uploaded document.
                  </div>
                </div>
              )}

              {!confirmationChecked && hasSelectedLots && !hasBlockingConflict && (
                <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <AlertCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700 flex-1" style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    <span className="font-medium">Confirmation required:</span> Please confirm all documents are present before starting.
                  </div>
                </div>
              )}

              {confirmationChecked && hasSelectedLots && !hasBlockingConflict && !hasUntypedDocuments && (
                <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-700 flex-1" style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    <span className="font-medium">Ready to proceed:</span> All requirements met.
                  </div>
                </div>
              )}

              {!hasSelectedLots && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-700 flex-1" style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    <span className="font-medium">Cannot proceed:</span> Please select an inspection lot before starting the BRR process.
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Clone confirmation dialog */}
      {showCloneDialog && selectedCloneSource && (() => {
        const isCrossBatchClone = batchNumber && batchNumber !== selectedCloneSource.batchId;
        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {isCrossBatchClone ? 'Use Template from Different Batch' : 'Clone BRR as New Review'}
            </h3>
            <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4">
              <div className="flex items-start gap-2">
                <AlertTriangle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  This creates a <strong>new BRR review</strong> pre-filled with data from
                  the selected review. The original BRR by {selectedCloneSource.userEmail} will
                  <strong> not be changed</strong>.
                </div>
              </div>
            </div>
            {isCrossBatchClone && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                <div className="flex items-start gap-2">
                  <FileText size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <strong>Cross-batch template:</strong> This will create a review for
                    batch <strong>{batchNumber}</strong> using settings from batch <strong>{selectedCloneSource.batchId}</strong>.
                    Documents will need to be uploaded for the new batch.
                  </div>
                </div>
              </div>
            )}
            <div className="text-sm text-gray-600 mb-4 space-y-1">
              <div><strong>Source:</strong> {selectedCloneSource.productName || 'No product'} ({selectedCloneSource.status})</div>
              <div><strong>Source Batch:</strong> {selectedCloneSource.batchId}</div>
              {isCrossBatchClone && <div><strong>Target Batch:</strong> {batchNumber}</div>}
              <div><strong>By:</strong> {selectedCloneSource.userEmail}</div>
              <div><strong>Last saved:</strong> {selectedCloneSource.savedAt ? new Date(selectedCloneSource.savedAt).toLocaleString() : 'N/A'}</div>
            </div>
            {cloneError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 mb-4">{cloneError}</div>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowCloneDialog(false); setCloneError(''); }}
                className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50"
                disabled={isCloning}
              >
                Cancel
              </button>
              <button
                onClick={handleClone}
                disabled={isCloning}
                className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isCloning ? <><Loader2 size={16} className="animate-spin" /> Cloning...</> : (isCrossBatchClone ? 'Apply Template to New Batch' : 'Create New Review from Template')}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Conflict detail modal */}
      {showConflictModal && conflictCheck && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4" style={{ overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'normal' }}>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <AlertCircle size={20} className="text-red-500" />
              BRR Conflict Detected
            </h3>

            {conflictCheck.exactMatch && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="font-medium text-red-800 mb-2">Exact Match Found</h4>
                <p className="text-sm text-red-700 mb-2">
                  A BRR already exists for this exact batch and inspection lot combination.
                  You cannot create a duplicate.
                </p>
                <div className="text-sm text-gray-700 space-y-1">
                  <p><span className="font-medium">Owner:</span> {conflictCheck.exactMatch.userEmail}</p>
                  <p><span className="font-medium">Status:</span> {conflictCheck.exactMatch.status}</p>
                  {conflictCheck.exactMatch.savedAt && (
                    <p><span className="font-medium">Last saved:</span> {new Date(conflictCheck.exactMatch.savedAt).toLocaleString()}</p>
                  )}
                  {conflictCheck.exactMatch.duplicateCount && conflictCheck.exactMatch.duplicateCount > 1 && (
                    <p className="text-amber-700 mt-2">
                      <span className="font-medium">Note:</span> {conflictCheck.exactMatch.duplicateCount} saves exist for this batch/lot.
                      Continuing will resume the most recent one.
                    </p>
                  )}
                </div>

                {/* Action buttons based on status */}
                <div className="mt-4 pt-3 border-t border-red-200 space-y-2">
                  {conflictDeleteError && (
                    <p className="text-sm text-red-600 mb-2">{conflictDeleteError}</p>
                  )}

                  {/* Draft: Show both cancel and continue options */}
                  {conflictCheck.exactMatch.status === 'draft' && (
                    <>
                      <button
                        onClick={async () => {
                          if (!conflictCheck.exactMatch) return;
                          setIsDeletingConflict(true);
                          setConflictDeleteError(null);
                          try {
                            await apiClient.deleteBRR(batchNumber, conflictCheck.exactMatch.sessionId);
                            // Clear conflict state and close modal
                            setConflictCheck(null);
                            setShowConflictModal(false);
                          } catch (error) {
                            console.error('Failed to delete conflicting draft:', error);
                            setConflictDeleteError('Failed to delete previous draft. Please try again.');
                          } finally {
                            setIsDeletingConflict(false);
                          }
                        }}
                        disabled={isDeletingConflict}
                        className="w-full py-2 px-3 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isDeletingConflict ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 size={14} />
                            Cancel previous review and begin again
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          if (!conflictCheck.exactMatch) return;
                          const conflictSessionId = conflictCheck.exactMatch.sessionId;

                          // Always set isResumingSession to prevent conflict check loop
                          // This must happen BEFORE onResumeSession to ensure the flag is set
                          // when the component re-renders with the new session
                          setIsResumingSession(true);
                          setConflictCheck(null);
                          setShowConflictModal(false);

                          if (onResumeSession) {
                            onResumeSession(conflictSessionId);
                          } else {
                            setActiveSessionId(conflictSessionId);
                            onSessionCreated?.(conflictSessionId);
                          }
                        }}
                        disabled={isDeletingConflict}
                        className="w-full py-2 px-3 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        <PlayCircle size={14} />
                        Continue working on review
                      </button>
                    </>
                  )}

                  {/* In-Progress: Show continue option */}
                  {conflictCheck.exactMatch.status === 'in-progress' && (
                    <button
                      onClick={() => {
                        if (!conflictCheck.exactMatch) return;
                        const conflictSessionId = conflictCheck.exactMatch.sessionId;

                        // Always set isResumingSession to prevent conflict check loop
                        setIsResumingSession(true);
                        setConflictCheck(null);
                        setShowConflictModal(false);

                        if (onResumeSession) {
                          onResumeSession(conflictSessionId);
                        } else {
                          setActiveSessionId(conflictSessionId);
                          onSessionCreated?.(conflictSessionId);
                        }
                      }}
                      className="w-full py-2 px-3 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 flex items-center justify-center gap-2"
                    >
                      <PlayCircle size={14} />
                      Continue working
                    </button>
                  )}

                  {/* Completed: Show load and edit option */}
                  {conflictCheck.exactMatch.status === 'completed' && (
                    <button
                      onClick={() => {
                        if (!conflictCheck.exactMatch) return;
                        const conflictSessionId = conflictCheck.exactMatch.sessionId;

                        // Always set isResumingSession to prevent conflict check loop
                        setIsResumingSession(true);
                        setConflictCheck(null);
                        setShowConflictModal(false);

                        if (onResumeSession) {
                          onResumeSession(conflictSessionId);
                        } else {
                          setActiveSessionId(conflictSessionId);
                          onSessionCreated?.(conflictSessionId);
                        }
                      }}
                      className="w-full py-2 px-3 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-2"
                    >
                      <FileText size={14} />
                      Load and edit existing review
                    </button>
                  )}
                </div>
              </div>
            )}

            {conflictCheck.partialMatches.length > 0 && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-medium text-amber-800 mb-2">Overlapping BRRs</h4>
                <p className="text-sm text-amber-700 mb-2">
                  The following BRRs share some inspection lots with your selection:
                </p>
                <ul className="text-sm text-gray-700 space-y-2">
                  {conflictCheck.partialMatches.map((match, idx) => (
                    <li key={idx} className="border-b border-amber-100 pb-2 last:border-0 last:pb-0">
                      <p><span className="font-medium">Owner:</span> {match.userEmail}</p>
                      <p><span className="font-medium">Status:</span> {match.status}</p>
                      <p><span className="font-medium">Overlapping lots:</span> {match.overlappingLots.join(', ')}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowConflictModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {previewBlobName && (
        <PdfHighlightModal
          apiClient={apiClient}
          blobName={previewBlobName}
          onClose={() => setPreviewBlobName(null)}
        />
      )}
    </div>
  );
};

export const StartBRRPage = React.memo(StartBRRPageComponent);
